import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Wave, Song } from '../../types';
import { useCanvasAnimation } from '../../hooks/useCanvasAnimation';
import { useAudio } from '../../hooks/useAudio';
import { OscilloscopeXYWebGL } from './OscilloscopeXYWebGL';
import { WaveformVisualizer } from './WaveformVisualizer';
import { WaveformType } from '../../types';
import { VisualizerRenderer } from './visualizers/VisualizerRenderer';
import { getVisualizerRegistry } from './visualizers/registry';
import './visualizers'; // Auto-register all visualizers

interface MasterOutputProps {
    waves: Wave[];
    speed: number;
    onSpeedChange: (speed: number) => void;
    onTogglePanels?: (open: boolean) => void;
    uploadedAudioData?: Float32Array | null;
    waveformData?: { left: Float32Array; right: Float32Array } | null;
    isUploadedAudioPlaying?: boolean;
    currentSong?: Song | null;
    showRawFrequencies?: boolean;
    onShowRawFrequenciesChange?: (show: boolean) => void;
}

type ViewMode = 'time' | 'waveform' | 'oscilloscope' | 'oscilloscope-xy' | 'lissajous' | 'xyz' | 'chladni' | 'fluid' | 'water' | 'galaxy' | 'sacred' | 'dmt' | 'audiobloom';

interface ViewState {
    zoom: number;
    pan: { x: number; y: number };
}

export const MasterOutput: React.FC<MasterOutputProps> = ({ 
    waves, 
    speed, 
    onSpeedChange, 
    onTogglePanels,
    uploadedAudioData,
    waveformData,
    isUploadedAudioPlaying,
    currentSong,
    showRawFrequencies = false,
    onShowRawFrequenciesChange
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const saved = localStorage.getItem('masterOutput_viewMode') as ViewMode | null;
        return saved || 'xyz';
    });

    const sampleWaveform = (waveform: WaveformType | undefined, angle: number): number => {
        switch (waveform) {
            case 'square': {
                const v = Math.sin(angle);
                return v >= 0 ? 1 : -1;
            }
            case 'triangle':
                return (2 / Math.PI) * Math.asin(Math.sin(angle));
            case 'sawtooth': {
                const norm = ((angle / (2 * Math.PI)) % 1 + 1) % 1;
                return 2 * norm - 1;
            }
            default:
                return Math.sin(angle);
        }
    };
    
    // Time visualizer: merged vs separate traces
    const [mergeChannels, setMergeChannels] = useState(() => {
        const saved = localStorage.getItem('masterOutput_mergeChannels');
        return saved ? JSON.parse(saved) : false;
    });
    
    // UI Controls visibility toggle
    const [showUIControls, setShowUIControls] = useState(() => {
        const saved = localStorage.getItem('masterOutput_showUIControls');
        return saved ? JSON.parse(saved) : true;
    });
    
    // Song info display toggle
    const [showSongInfo, setShowSongInfo] = useState(() => {
        const saved = localStorage.getItem('masterOutput_showSongInfo');
        return saved ? JSON.parse(saved) : true;
    });
    
    // Frequency display visibility toggle
    const [showFrequencyDisplay, setShowFrequencyDisplay] = useState(() => {
        const saved = localStorage.getItem('masterOutput_showFrequencyDisplay');
        return saved ? JSON.parse(saved) : true;
    });
    
    // Synthetic waveform for non-music oscilloscope
    const [syntheticWaveform, setSyntheticWaveform] = useState<{ left: Float32Array; right: Float32Array } | null>(null);
    
    // Song info position toggle (left/right) - persisted in localStorage
    const [songInfoPosition, setSongInfoPosition] = useState<'left' | 'right'>(() => {
        const saved = localStorage.getItem('masterOutput_songInfoPosition') as 'left' | 'right' | null;
        return (saved === 'left' || saved === 'right') ? saved : 'left';
    });
    
    // Frequency display position - persisted in localStorage
    const [frequencyDisplayPos, setFrequencyDisplayPos] = useState<{ x: number; y: number }>(() => {
        const saved = localStorage.getItem('masterOutput_frequencyDisplayPos');
        if (saved) return JSON.parse(saved);
        // Default to left-middle for better visibility
        return { x: 100, y: 250 };
    });
    const isFrequencyDragging = useRef(false);
    const frequencyDragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
    
    // Full-browser state
    const [isFullBrowser, setIsFullBrowser] = useState(false);
    
    // Speed Control - now comes from props
    const simTimeRef = useRef(0);
    
    const { isPlaying, toggleAudio } = useAudio(waves, speed);
    const [status, setStatus] = useState<{ text: string, type: 'normal' | 'destructive' | 'silence' }>({
        text: 'Initializing...', type: 'normal'
    });

    // Resizing State
    const [height, setHeight] = useState(() => {
        const saved = localStorage.getItem('masterOutput_height');
        return saved ? JSON.parse(saved) : 500;
    }); // Default pixel height
    const isResizing = useRef(false);

    // Auto Rotation State for XYZ
    const [isAutoRotating, setIsAutoRotating] = useState(() => {
        const saved = localStorage.getItem('masterOutput_isAutoRotating');
        return saved ? JSON.parse(saved) : true;
    });
    const autoRotRef = useRef(0);

    // Auto Cycle between visualizers
    const [isAutoCycle, setIsAutoCycle] = useState(() => {
        const saved = localStorage.getItem('masterOutput_isAutoCycle');
        return saved ? JSON.parse(saved) : false;
    });
    const [cycleDuration, setCycleDuration] = useState(() => {
        const saved = localStorage.getItem('masterOutput_cycleDuration');
        return saved ? JSON.parse(saved) : 30;
    });
    const cycleTimerRef = useRef<number | null>(null);
    const visualizerSequence: ViewMode[] = ['xyz', 'chladni', 'fluid', 'water', 'galaxy', 'sacred', 'dmt', 'audiobloom', 'time', 'waveform', 'oscilloscope', 'oscilloscope-xy', 'lissajous'];

    const advanceViewMode = () => {
        const idx = visualizerSequence.indexOf(viewMode);
        const next = idx === -1 ? visualizerSequence[0] : visualizerSequence[(idx + 1) % visualizerSequence.length];
        setViewMode(next);
    };

    const cycleVisualizer = (direction: 'next' | 'prev') => {
        const idx = visualizerSequence.indexOf(viewMode);
        if (idx === -1) {
            setViewMode(visualizerSequence[0]);
            return;
        }

        const nextIdx = direction === 'next'
            ? (idx + 1) % visualizerSequence.length
            : (idx - 1 + visualizerSequence.length) % visualizerSequence.length;

        setViewMode(visualizerSequence[nextIdx]);
    };

    useEffect(() => {
        if (isAutoCycle) {
            // Clear any existing timer before creating a new one
            if (cycleTimerRef.current !== null) {
                clearInterval(cycleTimerRef.current);
            }
            const id = window.setInterval(() => {
                advanceViewMode();
            }, Math.max(1, cycleDuration) * 1000);
            cycleTimerRef.current = id;
            return () => {
                if (cycleTimerRef.current !== null) {
                    clearInterval(cycleTimerRef.current);
                    cycleTimerRef.current = null;
                }
            };
        } else {
            if (cycleTimerRef.current !== null) {
                clearInterval(cycleTimerRef.current);
                cycleTimerRef.current = null;
            }
        }
    }, [isAutoCycle, cycleDuration, viewMode]);

    // View Controls State (Persisted per view)
    const [viewSettings, setViewSettings] = useState<Record<ViewMode, ViewState>>(() => {
        const saved = localStorage.getItem('masterOutput_viewSettings');
        if (saved) return JSON.parse(saved);
        return {
            time: { zoom: 1, pan: { x: 0, y: 0 } },
            waveform: { zoom: 1, pan: { x: 0, y: 0 } },
            oscilloscope: { zoom: 1, pan: { x: 0, y: 0 } },
            'oscilloscope-xy': { zoom: 1, pan: { x: 0, y: 0 } },
            lissajous: { zoom: 1, pan: { x: 0, y: 0 } },
            xyz: { zoom: 0.8, pan: { x: 0, y: 0 } },
            chladni: { zoom: 0.6, pan: { x: 0, y: 0 } },
            fluid: { zoom: 0.15, pan: { x: 0, y: 0 } },
            water: { zoom: 0.8, pan: { x: 0, y: 0 } },
            galaxy: { zoom: 1.0, pan: { x: 0, y: 0 } },
            sacred: { zoom: 1.0, pan: { x: 0, y: 0 } },
            dmt: { zoom: 1.0, pan: { x: 0, y: 0 } },
            audiobloom: { zoom: 0.7, pan: { x: 0, y: 0 } }
        };
    });

    // Derived values for current view
    const zoom = viewSettings[viewMode].zoom;
    const pan = viewSettings[viewMode].pan;

    const [isDragging, setIsDragging] = useState(false);
    const lastPos = useRef({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    
    // Mouse position for tooltips
    const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
    const [hoveredFrequency, setHoveredFrequency] = useState<{ freq: number; amp: number; channel: string; color: string } | null>(null);

    // Color mode toggle (wave colors vs channel-based stereo coloring)
    const [colorMode, setColorMode] = useState<'wave' | 'channel'>(() => {
        const saved = localStorage.getItem('masterOutput_colorMode') as 'wave' | 'channel' | null;
        return saved || 'wave';
    });

    // Base channel colors for frequency display
    const channelColors = useMemo(() => ({
        left: '#3b82f6',
        right: '#ec4899',
        both: '#10B981'
    }), []);

    // Music smoothing state (only used when audio is playing)
    const smoothedMusicDataRef = useRef<{ bassEnergy: number; midEnergy: number; trebleEnergy: number; totalEnergy: number } | null>(null);
    const rotationVelocityRef = useRef(0);
    const scaleVelocityRef = useRef(0);

    // Native Wheel Listener to prevent scrolling and handle zoom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheelNative = (e: WheelEvent) => {
            // Check if this event is from the OscilloscopeXY component
            const target = e.target as HTMLElement;
            const isOscilloscopeEvent = target && target.closest('[data-oscilloscope-xy="true"]');
            
            // Skip if event was already handled by a child or is from oscilloscope
            if (e.defaultPrevented || isOscilloscopeEvent) return;
            
            if (viewMode === 'oscilloscope' || viewMode === 'oscilloscope-xy' || viewMode === 'chladni' || viewMode === 'fluid' || viewMode === 'water' || viewMode === 'xyz' || viewMode === 'galaxy' || viewMode === 'sacred' || viewMode === 'dmt' || viewMode === 'audiobloom') {
                // Don't handle oscilloscope-xy here - let the component handle its own zoom
                if (viewMode === 'oscilloscope-xy') return;
                
                e.preventDefault();
                const delta = -e.deltaY * 0.001;
                
                setViewSettings(prev => {
                    const current = prev[viewMode];
                    const newZoom = Math.max(0.1, Math.min(10, current.zoom * (1 + delta)));
                    return {
                        ...prev,
                        [viewMode]: { ...current, zoom: newZoom }
                    };
                });
            }
        };

        // { passive: false } is required to allow preventDefault() to stop scrolling
        // Use capture phase to intercept before bubble phase
        container.addEventListener('wheel', handleWheelNative, { passive: false, capture: false });

        return () => {
            container.removeEventListener('wheel', handleWheelNative, { capture: false });
        };
    }, [viewMode]);

    // Save song info position to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('masterOutput_songInfoPosition', songInfoPosition);
    }, [songInfoPosition]);

    // Save frequency display position to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('masterOutput_frequencyDisplayPos', JSON.stringify(frequencyDisplayPos));
    }, [frequencyDisplayPos]);

    // Save all other preferences to localStorage when they change
    useEffect(() => {
        localStorage.setItem('masterOutput_viewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem('masterOutput_mergeChannels', JSON.stringify(mergeChannels));
    }, [mergeChannels]);

    useEffect(() => {
        localStorage.setItem('masterOutput_showUIControls', JSON.stringify(showUIControls));
    }, [showUIControls]);

    useEffect(() => {
        localStorage.setItem('masterOutput_showSongInfo', JSON.stringify(showSongInfo));
    }, [showSongInfo]);

    useEffect(() => {
        localStorage.setItem('masterOutput_showFrequencyDisplay', JSON.stringify(showFrequencyDisplay));
    }, [showFrequencyDisplay]);

    useEffect(() => {
        localStorage.setItem('masterOutput_height', JSON.stringify(height));
    }, [height]);

    useEffect(() => {
        localStorage.setItem('masterOutput_isAutoRotating', JSON.stringify(isAutoRotating));
    }, [isAutoRotating]);

    useEffect(() => {
        localStorage.setItem('masterOutput_isAutoCycle', JSON.stringify(isAutoCycle));
    }, [isAutoCycle]);

    useEffect(() => {
        localStorage.setItem('masterOutput_cycleDuration', JSON.stringify(cycleDuration));
    }, [cycleDuration]);

    useEffect(() => {
        localStorage.setItem('masterOutput_viewSettings', JSON.stringify(viewSettings));
    }, [viewSettings]);

    useEffect(() => {
        localStorage.setItem('masterOutput_colorMode', colorMode);
    }, [colorMode]);

    // Handle Resize Logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing.current) {
                // Check if frequency display is being dragged
                if (isFrequencyDragging.current) {
                    const dx = e.clientX - frequencyDragStart.current.x;
                    const dy = e.clientY - frequencyDragStart.current.y;
                    setFrequencyDisplayPos({
                        x: frequencyDragStart.current.posX + dx,
                        y: frequencyDragStart.current.posY + dy
                    });
                }
                return;
            }
            e.preventDefault();
            // Constrain height between 300px and 800px (or roughly 80% of viewport)
            const newHeight = Math.max(300, Math.min(window.innerHeight * 0.8, e.clientY - (containerRef.current?.getBoundingClientRect().top || 0)));
            setHeight(newHeight);
        };

        const handleMouseUp = () => {
            isResizing.current = false;
            isFrequencyDragging.current = false;
            document.body.style.cursor = 'default';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const startResize = (e: React.MouseEvent) => {
        isResizing.current = true;
        document.body.style.cursor = 'ns-resize';
    };

    // Helper to check if wave contributes
    const isActive = (w: Wave) => !w.muted && w.amp > 0;

    // Generate synthetic waveform for oscilloscope when no music is playing
    useEffect(() => {
        if (viewMode !== 'oscilloscope-xy' || waveformData) {
            setSyntheticWaveform(null);
            return;
        }

        const interval = setInterval(() => {
            const oscilloscopeActiveWaves = waves.filter(isActive);
            
            if (oscilloscopeActiveWaves.length === 0) {
                setSyntheticWaveform(null);
                return;
            }

            const time = simTimeRef.current;
            const pureLeftWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'left');
            const pureRightWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'right');
            const bothWaves = oscilloscopeActiveWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
            const autoPanWaves = oscilloscopeActiveWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);

            const numSamples = 2048;
            const leftBuffer = new Float32Array(numSamples);
            const rightBuffer = new Float32Array(numSamples);

            // Helper to compute XY values from waveforms
            const computeXY = (leftWaves: Wave[], rightWaves: Wave[], t: number) => {
                let xVal = 0;
                let yVal = 0;

                if (leftWaves.length > 0) {
                    let leftMaxAmp = leftWaves.reduce((s, w) => s + w.amp, 0);
                    leftMaxAmp = leftMaxAmp === 0 ? 1 : leftMaxAmp;
                    leftWaves.forEach(w => {
                        const wPhase = (w.phase * Math.PI) / 180;
                        const effectiveFreq = w.freq * speed;
                        const angle = 2 * Math.PI * effectiveFreq * t + wPhase;
                        xVal += (w.amp) * sampleWaveform(w.waveform, angle);
                    });
                    xVal = xVal / leftMaxAmp;
                }

                if (rightWaves.length > 0) {
                    let rightMaxAmp = rightWaves.reduce((s, w) => s + w.amp, 0);
                    rightMaxAmp = rightMaxAmp === 0 ? 1 : rightMaxAmp;
                    rightWaves.forEach(w => {
                        const wPhase = (w.phase * Math.PI) / 180;
                        const effectiveFreq = w.freq * speed;
                        const angle = 2 * Math.PI * effectiveFreq * t + wPhase;
                        yVal += (w.amp) * sampleWaveform(w.waveform, angle);
                    });
                    yVal = yVal / rightMaxAmp;
                }

                return { x: xVal, y: yVal };
            };

            // Combine all wave types
            const allLeftWaves = [...pureLeftWaves, ...bothWaves];
            const allRightWaves = [...pureRightWaves, ...bothWaves];
            
            // Add auto-pan waves distributed to left/right based on current pan
            autoPanWaves.forEach(w => {
                const autoPanRate = w.autoPanRate || 0;
                const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                const panVal = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                const leftGain = (1 - panVal) / 2;
                const rightGain = (1 + panVal) / 2;
                
                if (leftGain > 0.1) allLeftWaves.push({ ...w, amp: w.amp * leftGain });
                if (rightGain > 0.1) allRightWaves.push({ ...w, amp: w.amp * rightGain });
            });

            // Generate samples
            for (let i = 0; i < numSamples; i++) {
                const t = time + (i / numSamples) * 0.05; // Small time window
                const { x, y } = computeXY(allLeftWaves, allRightWaves, t);
                leftBuffer[i] = x;
                rightBuffer[i] = y;
            }

            setSyntheticWaveform({ left: leftBuffer, right: rightBuffer });
        }, 16); // ~60fps

        return () => clearInterval(interval);
    }, [viewMode, waveformData, waves, speed]);

    // Frequency display data - includes color per entry so it can respect color mode
    const [leftFrequencies, setLeftFrequencies] = useState<Array<{ freq: number; amp: number; color?: string }>>([]);
    const [rightFrequencies, setRightFrequencies] = useState<Array<{ freq: number; amp: number; color?: string }>>([]);
    const [bothFrequencies, setBothFrequencies] = useState<Array<{ freq: number; amp: number; color?: string }>>([]);
    
    const hasAnyFrequency = leftFrequencies.length > 0 || rightFrequencies.length > 0 || bothFrequencies.length > 0;

    // Update frequency display for all views - handle both music playing and configured waves
    useEffect(() => {
        // When music is playing, show audio frequencies
        if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
            const leftFreqMap = new Map<number, { amp: number; color: string }>();
            const rightFreqMap = new Map<number, { amp: number; color: string }>();
            const bothFreqMap = new Map<number, { amp: number; color: string }>();

            const colorForChannel = (channel: number) => {
                // Uploaded audio has no wave color; use channel palette regardless of colorMode
                if (channel === 1) return channelColors.left;
                if (channel === 2) return channelColors.right;
                return channelColors.both;
            };
            
            // Parse audio data and group by frequency and channel
            for (let i = 0; i < uploadedAudioData.length; i += 3) {
                if (i + 2 < uploadedAudioData.length) {
                    const freq = uploadedAudioData[i];
                    const amp = uploadedAudioData[i + 1];
                    const channel = uploadedAudioData[i + 2];
                    
                    if (amp > 0.01) { // Only include significant amplitudes
                        if (channel === 1) { // Left
                            const existing = leftFreqMap.get(freq);
                            const nextAmp = (existing?.amp || 0) + amp;
                            leftFreqMap.set(freq, { amp: nextAmp, color: colorForChannel(1) });
                        } else if (channel === 2) { // Right
                            const existing = rightFreqMap.get(freq);
                            const nextAmp = (existing?.amp || 0) + amp;
                            rightFreqMap.set(freq, { amp: nextAmp, color: colorForChannel(2) });
                        } else { // Both (0)
                            const existing = bothFreqMap.get(freq);
                            const nextAmp = (existing?.amp || 0) + amp;
                            bothFreqMap.set(freq, { amp: nextAmp, color: colorForChannel(0) });
                        }
                    }
                }
            }
            
            // Convert to sorted arrays (by amplitude, descending) - show top 12
            setLeftFrequencies(Array.from(leftFreqMap.entries())
                .map(([freq, { amp, color }]) => ({ freq, amp, color }))
                .sort((a, b) => b.amp - a.amp)
                .slice(0, 12)
            );
            setRightFrequencies(Array.from(rightFreqMap.entries())
                .map(([freq, { amp, color }]) => ({ freq, amp, color }))
                .sort((a, b) => b.amp - a.amp)
                .slice(0, 12)
            );
            setBothFrequencies(Array.from(bothFreqMap.entries())
                .map(([freq, { amp, color }]) => ({ freq, amp, color }))
                .sort((a, b) => b.amp - a.amp)
                .slice(0, 12)
            );
        } else {
            // When no music is playing, show configured wave frequencies
            const activeWaves = waves.filter(w => !w.muted);
            const leftWavesForDisplay = activeWaves.filter(w => w.channel === 'left');
            const rightWavesForDisplay = activeWaves.filter(w => w.channel === 'right');
            const bothWavesForDisplay = activeWaves.filter(w => (w.channel || 'both') === 'both');
            
            // Group by frequency and sum amplitudes; pick color based on colorMode
            const groupByFreq = (wavesArr: Wave[], channelDefault: string) => {
                const freqMap = new Map<number, { amp: number; color?: string }>();
                wavesArr.forEach(w => {
                    const existing = freqMap.get(w.freq);
                    const nextAmp = (existing?.amp || 0) + w.amp;
                    // choose color: wave color when colorMode is wave, else channel default
                    const nextColor = colorMode === 'wave' ? (w.color || channelDefault) : channelDefault;
                    // keep color of dominant contributor
                    if (!existing || w.amp > existing.amp) {
                        freqMap.set(w.freq, { amp: nextAmp, color: nextColor });
                    } else {
                        freqMap.set(w.freq, { amp: nextAmp, color: existing.color });
                    }
                });
                return Array.from(freqMap.entries())
                    .map(([freq, { amp, color }]) => ({ freq, amp, color }))
                    .sort((a, b) => b.amp - a.amp);
            };
            
            setLeftFrequencies(groupByFreq(leftWavesForDisplay, channelColors.left));
            setRightFrequencies(groupByFreq(rightWavesForDisplay, channelColors.right));
            setBothFrequencies(groupByFreq(bothWavesForDisplay, channelColors.both));
        }
    }, [isUploadedAudioPlaying, uploadedAudioData, waves, colorMode, channelColors]);

    const canvasRef = useCanvasAnimation((ctx, _totalTime, deltaTime, width, height) => {
        const cx = width / 2;
        const cy = height / 2;
        
        // Create temporary waves from uploaded audio frequency data
        let effectiveWaves = waves;
        let oscilloscopeWaves = waves; // Oscilloscope always shows raw audio
        
        // When music is playing, prepare both raw and filtered versions
        if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
            // RAW MODE: Show only the music frequencies
            // Parse frequency data: [freq1, amp1, channel1, freq2, amp2, channel2, ...]
            // channel: 0 = both, 1 = left, 2 = right
            const audioWaves: Wave[] = [];
            const leftColor = '#3B82F6';   // Blue
            const rightColor = '#EC4899';  // Pink
            const centerColor = '#10B981'; // Emerald
            
            for (let i = 0; i < uploadedAudioData.length; i += 3) {
                if (i + 2 < uploadedAudioData.length) {
                    const frequency = uploadedAudioData[i];
                    const amplitude = uploadedAudioData[i + 1];
                    const channelId = uploadedAudioData[i + 2];
                    
                    if (frequency > 0 && amplitude > 0) {
                        // Map audio frequency to visualization frequency
                        // Audio: 20Hz - 20kHz, Visualization: typically 1-20 Hz
                        // Use log scale to map audio frequencies to visual frequencies
                        const visualFreq = Math.max(1, Math.min(20, Math.log10(frequency / 20) * 3 + 1));
                        
                        // Determine channel and color based on channelId
                        let channel: 'left' | 'right' | 'both' = 'both';
                        let color = centerColor;
                        
                        if (channelId === 1) {
                            channel = 'left';
                            color = leftColor;
                        } else if (channelId === 2) {
                            channel = 'right';
                            color = rightColor;
                        }
                        
                        audioWaves.push({
                            id: 1000 + i,
                            freq: visualFreq,
                            amp: amplitude * 100, // Convert 0-1 to 0-100
                            phase: 0,
                            color: color,
                            muted: false,
                            channel: channel,
                            originalFreq: frequency // Store original audio frequency for display
                        });
                    }
                }
            }
            
            // Oscilloscope always uses raw audio waves
            if (audioWaves.length > 0) {
                oscilloscopeWaves = audioWaves;
            }
            
            // For other views, use raw or filtered based on toggle
            if (showRawFrequencies) {
                // RAW MODE for other views too
                if (audioWaves.length > 0) {
                    effectiveWaves = audioWaves;
                }
            } else {
                // FILTERED MODE: Use configured waves as base, modulate music on top
                // Keep the configured waves as the foundation
                const baseWaves = waves.map(wave => ({ ...wave }));
                
                // Add music-driven modulation waves that correspond to configured frequencies
                const modulationWaves: Wave[] = [];
                
                // For each configured wave, create a modulation wave based on nearby music frequencies
                waves.forEach((wave, index) => {
                    if (wave.muted) return;
                    
                    // Find audio energy near this wave's frequency
                    let totalEnergy = 0;
                    let matchCount = 0;
                    let channelEnergy: { [key: string]: number } = { left: 0, right: 0, both: 0 };
                    let matchedAudioFrequencies: number[] = []; // Track actual audio frequencies that matched
                    
                    // Convert wave visual frequency back to approximate audio frequency range
                    const audioFreqEstimate = 20 * Math.pow(10, (wave.freq - 1) / 3);
                    
                    // Look for audio frequencies within +/- 50% of this frequency
                    const freqTolerance = 0.5;
                    
                    for (let i = 0; i < uploadedAudioData.length; i += 3) {
                        if (i + 2 < uploadedAudioData.length) {
                            const audioFreq = uploadedAudioData[i];
                            const audioAmp = uploadedAudioData[i + 1];
                            const audioChannel = uploadedAudioData[i + 2];
                            
                            // Check if this audio frequency is close to our wave's frequency
                            const freqRatio = audioFreq / audioFreqEstimate;
                            if (freqRatio >= (1 - freqTolerance) && freqRatio <= (1 + freqTolerance)) {
                                totalEnergy += audioAmp;
                                matchCount++;
                                matchedAudioFrequencies.push(audioFreq);
                                
                                // Track energy per channel
                                if (audioChannel === 0) channelEnergy.both += audioAmp;
                                else if (audioChannel === 1) channelEnergy.left += audioAmp;
                                else if (audioChannel === 2) channelEnergy.right += audioAmp;
                            }
                        }
                    }
                    
                    // If we found matching frequencies, create a modulation wave
                    if (matchCount > 0 && totalEnergy > 0.05) {
                        const avgEnergy = totalEnergy / matchCount;
                        const modulationAmp = avgEnergy * 80; // Scale to 0-80 range for modulation
                        
                        // Find the dominant (most energetic) audio frequency for display
                        let dominantAudioFreq = 0;
                        if (matchedAudioFrequencies.length > 0) {
                            // Use the average of matched frequencies as the display frequency
                            dominantAudioFreq = matchedAudioFrequencies.reduce((a, b) => a + b, 0) / matchedAudioFrequencies.length;
                        }
                        
                        // Use the dominant channel from the audio data
                        let dominantChannel: 'left' | 'right' | 'both' = wave.channel || 'both';
                        if (channelEnergy.left > channelEnergy.right && channelEnergy.left > channelEnergy.both * 0.5) {
                            dominantChannel = 'left';
                        } else if (channelEnergy.right > channelEnergy.left && channelEnergy.right > channelEnergy.both * 0.5) {
                            dominantChannel = 'right';
                        }
                        
                        modulationWaves.push({
                            id: 2000 + index,
                            freq: wave.freq,
                            amp: modulationAmp,
                            phase: wave.phase + 90, // Offset phase slightly for visual interest
                            color: wave.color,
                            muted: false,
                            channel: dominantChannel,
                            originalFreq: dominantAudioFreq > 0 ? dominantAudioFreq : undefined // Store actual audio frequency for display
                        });
                    }
                });
                
                // Combine base waves with modulation waves for other views
                effectiveWaves = [...baseWaves, ...modulationWaves];
            }
        }
        
        const activeWaves = effectiveWaves.filter(isActive);

        // If no active waves, just clear and render empty canvas (don't crash)
        if (activeWaves.length === 0) {
            ctx.fillStyle = (viewMode === 'fluid' || viewMode === 'xyz') ? '#0b0f19' : '#00000000';
            ctx.fillRect(0, 0, width, height);
            if (viewMode !== 'fluid' && viewMode !== 'xyz') {
                ctx.clearRect(0, 0, width, height);
            }
            // Ensure any prior transforms are cleared on empty frames
            try { ctx.restore(); } catch {}
            return;
        }

        // === MUSIC-REACTIVE FREQUENCY ANALYZER ===
        // Extract frequency bands from uploaded audio for music visualization
        const getMusicFrequencyData = () => {
            if (!isUploadedAudioPlaying || !uploadedAudioData || uploadedAudioData.length === 0) {
                return null;
            }
            
            // Bass: 20-250 Hz, Mid: 250-4000 Hz, Treble: 4000-20000 Hz
            let bassEnergy = 0;
            let midEnergy = 0;
            let trebleEnergy = 0;
            let totalEnergy = 0;
            
            for (let i = 0; i < uploadedAudioData.length; i += 3) {
                if (i + 2 < uploadedAudioData.length) {
                    const freq = uploadedAudioData[i];
                    const amp = uploadedAudioData[i + 1];
                    const energy = amp * 100;
                    
                    if (freq >= 20 && freq < 250) {
                        bassEnergy += energy;
                    } else if (freq >= 250 && freq < 4000) {
                        midEnergy += energy;
                    } else if (freq >= 4000) {
                        trebleEnergy += energy;
                    }
                    
                    totalEnergy += energy;
                }
            }
            
            return { bassEnergy, midEnergy, trebleEnergy, totalEnergy };
        };
        
        const musicData = getMusicFrequencyData();

        // === MUSIC SMOOTHING (only when audio is playing) ===
        let smoothedMusicData = musicData;
        if (musicData && isUploadedAudioPlaying) {
            // Exponential moving average for smooth energy transitions
            const smoothingFactor = 0.25; // 0 = no smoothing, 1 = instant response
            
            if (!smoothedMusicDataRef.current) {
                // Initialize with current values
                smoothedMusicDataRef.current = { ...musicData };
            } else {
                // Smooth each energy band separately
                smoothedMusicDataRef.current.bassEnergy = 
                    smoothedMusicDataRef.current.bassEnergy * (1 - smoothingFactor) + musicData.bassEnergy * smoothingFactor;
                smoothedMusicDataRef.current.midEnergy = 
                    smoothedMusicDataRef.current.midEnergy * (1 - smoothingFactor) + musicData.midEnergy * smoothingFactor;
                smoothedMusicDataRef.current.trebleEnergy = 
                    smoothedMusicDataRef.current.trebleEnergy * (1 - smoothingFactor) + musicData.trebleEnergy * smoothingFactor;
                smoothedMusicDataRef.current.totalEnergy = 
                    smoothedMusicDataRef.current.totalEnergy * (1 - smoothingFactor) + musicData.totalEnergy * smoothingFactor;
            }
            
            smoothedMusicData = smoothedMusicDataRef.current;
        } else {
            // Reset smoothing when not playing music
            smoothedMusicDataRef.current = null;
        }

        // Update Simulation Time (normal progression - speed affects frequencies instead)
        simTimeRef.current += deltaTime;
        const time = simTimeRef.current;

        // Clear canvas
        if (viewMode !== 'chladni' && viewMode !== 'water' && viewMode !== 'oscilloscope-xy' && viewMode !== 'waveform') {
             // For fluid/xyz mode, we might want a darker clear or gradient
            ctx.fillStyle = (viewMode === 'fluid' || viewMode === 'xyz') ? '#0b0f19' : '#00000000'; 
            ctx.fillRect(0, 0, width, height);
            if (viewMode !== 'fluid' && viewMode !== 'xyz') ctx.clearRect(0, 0, width, height);
        }

        // --- TIME DOMAIN ---
        if (viewMode === 'time') {
            // Grid Axis
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, cy);
            ctx.lineTo(width, cy);
            ctx.stroke();

            if (activeWaves.length === 0) {
                return;
            }

            // Separate waves by channel
            const leftWaves = activeWaves.filter(w => (w.channel || 'both') !== 'right');
            const rightWaves = activeWaves.filter(w => (w.channel || 'both') !== 'left');
            const autoPanWaves = activeWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);
            const staticWaves = activeWaves.filter(w => !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));

            // Check if we have distinct stereo channels (excluding auto-pan waves)
            const staticLeftWaves = staticWaves.filter(w => (w.channel || 'both') !== 'right');
            const staticRightWaves = staticWaves.filter(w => (w.channel || 'both') !== 'left');
            const hasDistinctChannels = staticLeftWaves.length > 0 && staticRightWaves.length > 0 &&
                (staticLeftWaves.some(w => w.channel === 'left') || staticRightWaves.some(w => w.channel === 'right'));
            const hasChannelSpecificWaves = staticWaves.some(w => w.channel === 'left' || w.channel === 'right');

            // Helper to interpolate between Blue and Pink based on pan
            const getPanColor = (pan: number): string => {
                const t = (pan + 1) / 2;
                const r = Math.floor(59 + (236 - 59) * t);
                const g = Math.floor(130 + (72 - 130) * t);
                const b = Math.floor(246 + (153 - 246) * t);
                return `rgb(${r}, ${g}, ${b})`;
            };

            // Helper to draw a waveform for given waves
            const drawWaveform = (wavesToDraw: Wave[], color: string, yOffset: number = 0) => {
                if (wavesToDraw.length === 0) return;
                
                const maxPossibleAmp = wavesToDraw.reduce((sum, w) => sum + w.amp, 0);
                const scale = maxPossibleAmp > 120 ? (120 / maxPossibleAmp) : 1;

                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.lineWidth = 2.5;
                ctx.lineJoin = 'round';
                ctx.shadowBlur = 8;
                ctx.shadowColor = color;

                for (let x = 0; x < width; x += 1) {
                    const t = x / width;
                    let ySum = 0;

                    for (const wave of wavesToDraw) {
                        const phaseRad = (wave.phase * Math.PI) / 180;
                        const effectiveFreq = wave.freq * speed;
                        const angle = 2 * Math.PI * effectiveFreq * t + phaseRad + (time * 2);
                        ySum += (wave.amp / 100) * (height / 2.5) * sampleWaveform(wave.waveform, angle);
                    }

                    ySum *= scale;

                    if (x === 0) ctx.moveTo(x, cy - ySum + yOffset);
                    else ctx.lineTo(x, cy - ySum + yOffset);
                }
                ctx.stroke();
            };

            if (!mergeChannels && (hasDistinctChannels || hasChannelSpecificWaves || autoPanWaves.length > 0)) {
                // STEREO MODE: Draw separate traces (only when not merged)

                // Draw left channel (blue) - slight offset up
                const pureLeftWaves = staticWaves.filter(w => w.channel === 'left');
                if (pureLeftWaves.length > 0) {
                    drawWaveform(pureLeftWaves, '#3b82f6', hasDistinctChannels ? -15 : 0);
                }

                // Draw right channel (pink) - slight offset down
                const pureRightWaves = staticWaves.filter(w => w.channel === 'right');
                if (pureRightWaves.length > 0) {
                    drawWaveform(pureRightWaves, '#ec4899', hasDistinctChannels ? 15 : 0);
                }

                // Draw center/both waves (green)
                const bothWaves = staticWaves.filter(w => (w.channel || 'both') === 'both');
                if (bothWaves.length > 0) {
                    drawWaveform(bothWaves, '#10B981', 0);
                }

                // Draw auto-pan waves with dynamic color AND vertical movement
                if (autoPanWaves.length > 0) {
                    // Get current weighted pan position
                    let weightedPan = 0;
                    let totalAmp = 0;
                    for (const w of autoPanWaves) {
                        const autoPanRate = w.autoPanRate || 0;
                        const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                        const pan = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                        weightedPan += w.amp * pan;
                        totalAmp += w.amp;
                    }
                    const currentPan = totalAmp > 0 ? weightedPan / totalAmp : 0;
                    const panColor = getPanColor(currentPan);
                    
                    // Move up (negative) when panning left, down (positive) when panning right
                    // pan: -1 (left) = -40px, pan: +1 (right) = +40px
                    const panYOffset = currentPan * 40;
                    
                    drawWaveform(autoPanWaves, panColor, panYOffset);
                }

                // Add channel labels
                ctx.shadowBlur = 0;
                ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
                ctx.font = '11px sans-serif';
                if (hasDistinctChannels) {
                    ctx.fillText('L', 8, cy - 15);
                    ctx.fillText('R', 8, cy + 20);
                }
            } else {
                // MONO MODE: Single emerald trace
                ctx.beginPath();
                ctx.strokeStyle = '#10B981';
                ctx.lineWidth = 3;
                ctx.lineJoin = 'round';
                ctx.shadowBlur = 8;
                ctx.shadowColor = ctx.strokeStyle;

                const maxPossibleAmp = activeWaves.reduce((sum, w) => sum + w.amp, 0);
                const scale = maxPossibleAmp > 120 ? (120 / maxPossibleAmp) : 1;

                for (let x = 0; x < width; x += 1) {
                    const t = x / width;
                    let ySum = 0;

                    for (const wave of activeWaves) {
                        const phaseRad = (wave.phase * Math.PI) / 180;
                        const effectiveFreq = wave.freq * speed;
                        const angle = 2 * Math.PI * effectiveFreq * t + phaseRad + (time * 2);
                        ySum += (wave.amp / 100) * (height / 2.5) * sampleWaveform(wave.waveform, angle);
                    }

                    ySum *= scale;

                    if (x === 0) ctx.moveTo(x, cy - ySum);
                    else ctx.lineTo(x, cy - ySum);
                }
                ctx.stroke();
            }
            ctx.shadowBlur = 0;
        } 
        
        // --- OSCILLOSCOPE (Frequency spectrum) ---
        else if (viewMode === 'oscilloscope') {
            // Use oscilloscopeWaves which always shows raw audio when music is playing
            const oscilloscopeActiveWaves = oscilloscopeWaves.filter(isActive);
            
            if (oscilloscopeActiveWaves.length === 0) {
                return;
            }

            // Calculate frequency range based on zoom level
            const maxFreq = Math.max(...oscilloscopeActiveWaves.map(w => w.freq), 1);
            // Zoom out (zoom < 1) = larger visible range, Zoom in (zoom > 1) = smaller visible range
            const visibleFreqRange = (maxFreq * 1.2) / zoom;
            
            // Total frequency range available (for panning limits)
            const totalFreqRange = maxFreq * 2; // Allow panning through a wide range
            
            // Pan offset in frequency units - pan scrolls through the available range
            // Clamp pan so we don't scroll past 0 or too far right
            const maxPanOffset = Math.max(0, totalFreqRange - visibleFreqRange);
            const freqPanOffset = Math.max(0, Math.min((pan.x / width) * visibleFreqRange, maxPanOffset));
            const minVisibleFreq = freqPanOffset;
            const adjustedMaxVisibleFreq = freqPanOffset + visibleFreqRange;

            // Draw background grid
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
            ctx.lineWidth = 1;
            
            // Horizontal grid lines
            for (let i = 0; i <= 10; i++) {
                const y = (height / 10) * i;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
            }
            
            // Vertical grid lines based on frequency range
            // Determine nice frequency intervals
            const freqRange = adjustedMaxVisibleFreq - minVisibleFreq;
            let freqInterval = 10;
            if (freqRange > 1000) freqInterval = 100;
            else if (freqRange > 500) freqInterval = 50;
            else if (freqRange > 200) freqInterval = 25;
            else if (freqRange > 100) freqInterval = 10;
            else if (freqRange > 50) freqInterval = 5;
            else freqInterval = 1;
            
            // Draw grid lines at frequency intervals
            const startFreq = Math.ceil(minVisibleFreq / freqInterval) * freqInterval;
            for (let freq = startFreq; freq <= adjustedMaxVisibleFreq; freq += freqInterval) {
                const freqNormalized = (freq - minVisibleFreq) / freqRange;
                const x = freqNormalized * width;
                
                if (x >= 0 && x <= width) {
                    ctx.beginPath();
                    ctx.moveTo(x, 0);
                    ctx.lineTo(x, height);
                    ctx.stroke();
                }
            }

            // Draw center axis
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, cy);
            ctx.lineTo(width, cy);
            ctx.stroke();

            // Separate waves by channel
            const pureLeftWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'left');
            const pureRightWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'right');
            const bothWaves = oscilloscopeActiveWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
            const autoPanWaves = oscilloscopeActiveWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);
            const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

            // Helper to interpolate between Blue and Pink based on pan
            const getPanColor = (panVal: number): string => {
                const t = (panVal + 1) / 2;
                const r = Math.floor(59 + (236 - 59) * t);
                const g = Math.floor(130 + (72 - 130) * t);
                const b = Math.floor(246 + (153 - 246) * t);
                return `rgb(${r}, ${g}, ${b})`;
            };

            // Draw frequency spectrum bars
            const drawSpectrum = (wavesToDraw: Wave[], color: string, channelName: string) => {
                if (wavesToDraw.length === 0) return;

                // Group waves by frequency and sum their amplitudes
                const frequencyBins = new Map<number, number>();
                
                wavesToDraw.forEach(wave => {
                    const currentAmp = frequencyBins.get(wave.freq) || 0;
                    frequencyBins.set(wave.freq, currentAmp + wave.amp);
                });

                ctx.shadowBlur = 8;
                ctx.shadowColor = color;

                // Draw combined bars for each unique frequency
                frequencyBins.forEach((totalAmp, freq) => {
                    // X position based on frequency relative to visible range
                    // Use the same freqRange calculation as grid lines for consistent alignment
                    const freqNormalized = (freq - minVisibleFreq) / freqRange;
                    const x = freqNormalized * width;
                    
                    // Only draw if visible
                    if (x >= -10 && x <= width + 10) {
                        // Height based on combined amplitude
                        const barHeight = (totalAmp / 100) * (cy - 20);
                        
                        // Check if mouse is hovering over this bar
                        if (mousePos && Math.abs(mousePos.x - x) < 8 && mousePos.y >= cy - barHeight && mousePos.y <= cy) {
                            // Use the original frequency from the first wave in this bin if available
                            const firstWaveWithFreq = wavesToDraw.find(w => w.freq === freq);
                            const displayFreq = firstWaveWithFreq?.originalFreq ?? freq;
                            setHoveredFrequency({ freq: displayFreq, amp: totalAmp, channel: channelName, color });
                        }
                        
                        // Draw bar standing on the center line
                        ctx.fillStyle = color;
                        ctx.fillRect(x - 4, cy - barHeight, 8, barHeight);
                        
                        // Draw outline
                        ctx.strokeStyle = color;
                        ctx.lineWidth = 1;
                        ctx.strokeRect(x - 4, cy - barHeight, 8, barHeight);
                    }
                });

                ctx.shadowBlur = 0;
            };

            // Reset hovered frequency at start of each frame
            setHoveredFrequency(null);

            if (hasDistinctChannels) {
                // Draw left channel (blue)
                drawSpectrum(pureLeftWaves, '#3b82f6', 'Left');

                // Draw right channel (pink)
                drawSpectrum(pureRightWaves, '#ec4899', 'Right');

                // Draw both/center waves (emerald)
                drawSpectrum(bothWaves, '#10B981', 'Center');
            } else {
                // Mono mode: single spectrum
                drawSpectrum(oscilloscopeActiveWaves, '#10B981', 'Mono');
            }

            // Draw auto-pan waves with dynamic color
            if (autoPanWaves.length > 0) {
                let weightedPan = 0;
                let totalAmp = 0;
                for (const w of autoPanWaves) {
                    const autoPanRate = w.autoPanRate || 0;
                    const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                    const panVal = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                    weightedPan += w.amp * panVal;
                    totalAmp += w.amp;
                }
                const currentPan = totalAmp > 0 ? weightedPan / totalAmp : 0;
                const panColor = getPanColor(currentPan);
                drawSpectrum(autoPanWaves, panColor, 'Auto-Pan');
            }
            
            // Draw tooltip for hovered frequency
            if (hoveredFrequency && mousePos) {
                const tooltipPadding = 8;
                const tooltipText = `${hoveredFrequency.freq.toFixed(1)}Hz (${hoveredFrequency.channel})`;
                ctx.font = 'bold 12px sans-serif';
                const textWidth = ctx.measureText(tooltipText).width;
                const tooltipWidth = textWidth + tooltipPadding * 2;
                const tooltipHeight = 24;
                
                // Position tooltip above mouse
                let tooltipX = mousePos.x - tooltipWidth / 2;
                let tooltipY = mousePos.y - tooltipHeight - 10;
                
                // Keep tooltip within canvas bounds
                tooltipX = Math.max(5, Math.min(tooltipX, width - tooltipWidth - 5));
                tooltipY = Math.max(5, tooltipY);
                
                // Draw tooltip background
                ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
                ctx.strokeStyle = hoveredFrequency.color;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, 4);
                ctx.fill();
                ctx.stroke();
                
                // Draw tooltip text
                ctx.fillStyle = hoveredFrequency.color;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(tooltipText, tooltipX + tooltipWidth / 2, tooltipY + tooltipHeight / 2);
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
            }

            // Draw frequency labels at grid line positions only
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.font = '10px sans-serif';
            
            // Draw labels at the same positions as grid lines
            const startFreqLabel = Math.ceil(minVisibleFreq / freqInterval) * freqInterval;
            for (let freq = startFreqLabel; freq <= adjustedMaxVisibleFreq; freq += freqInterval) {
                const freqNormalized = (freq - minVisibleFreq) / freqRange;
                const xLabel = freqNormalized * width;
                
                // Only draw labels that are visible
                if (xLabel >= 0 && xLabel <= width) {
                    ctx.fillText(`${Math.round(freq)}Hz`, xLabel - 15, height - 5);
                }
            }
        }

        // --- OSCILLOSCOPE XY MODE (Real-time waveform) ---
        else if (viewMode === 'oscilloscope-xy') {
            // Enhanced Phosphor fade - more gradual decay for realistic CRT persistence
            // Real phosphor screens have multi-stage decay
            ctx.fillStyle = 'rgba(0, 0, 0, 1)'; // Very fast decay
            ctx.fillRect(0, 0, width, height);
            
            // Add subtle green tint to background for classic CRT look
            ctx.fillStyle = 'rgba(0, 20, 10, 0.01)';
            ctx.fillRect(0, 0, width, height);
            
            // Subtle crosshairs
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx, 0);
            ctx.lineTo(cx, height);
            ctx.moveTo(0, cy);
            ctx.lineTo(width, cy);
            ctx.stroke();

            const margin = 20;
            const radiusX = (width / 2) - margin;
            const radiusY = (height / 2) - margin;

            // === TRUE OSCILLOSCOPE MODE: Use raw waveform data when available ===
            if (waveformData && waveformData.left.length > 0) {
                const leftSamples = waveformData.left;
                const rightSamples = waveformData.right;
                const numSamples = Math.min(leftSamples.length, rightSamples.length);
                
                // Simple single-pass rendering with interpolation for higher visual resolution
                ctx.fillStyle = 'rgba(200, 255, 200, 0.95)';
                ctx.shadowBlur = 3;
                ctx.shadowColor = '#00FF00';
                
                // Interpolate between samples for denser dot coverage
                const interpolationSteps = 3; // Creates intermediate points
                
                for (let i = 0; i < numSamples - 1; i++) {
                    const x1 = leftSamples[i];
                    const y1 = rightSamples[i];
                    const x2 = leftSamples[i + 1];
                    const y2 = rightSamples[i + 1];
                    
                    for (let step = 0; step <= interpolationSteps; step++) {
                        const t = step / interpolationSteps;
                        const x = x1 + (x2 - x1) * t;
                        const y = y1 + (y2 - y1) * t;
                        
                        const plotX = cx + (x * radiusX);
                        const plotY = cy - (y * radiusY);
                        
                        ctx.fillRect(plotX - 0.5, plotY - 0.5, 1, 1);
                    }
                }
                
                ctx.shadowBlur = 0;
            } else {
                // Fallback: use synthesized waves when no raw waveform data
                const oscilloscopeActiveWaves = oscilloscopeWaves.filter(isActive);
                
                if (oscilloscopeActiveWaves.length === 0) {
                    // Show "No audio" message
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                    ctx.font = '14px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('Play music to see oscilloscope display', cx, cy);
                    ctx.textAlign = 'left';
                    return;
                }

                const segments = 2000;
                const history = 2.0;

                // Separate waves by channel
                const pureLeftWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'left');
                const pureRightWaves = oscilloscopeActiveWaves.filter(w => w.channel === 'right');
                const bothWaves = oscilloscopeActiveWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
                const autoPanWaves = oscilloscopeActiveWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);

                // Helper to interpolate between Blue and Pink based on pan
                const getPanColor = (panVal: number): string => {
                    const t = (panVal + 1) / 2;
                    const r = Math.floor(59 + (236 - 59) * t);
                    const g = Math.floor(130 + (72 - 130) * t);
                    const b = Math.floor(246 + (153 - 246) * t);
                    return `rgb(${r}, ${g}, ${b})`;
                };

                // Helper to compute XY values from waveforms
                const computeXY = (leftWaves: Wave[], rightWaves: Wave[], t: number) => {
                    let xVal = 0;
                    let yVal = 0;

                    if (leftWaves.length > 0) {
                        let leftMaxAmp = leftWaves.reduce((s, w) => s + w.amp, 0);
                        leftMaxAmp = leftMaxAmp === 0 ? 1 : leftMaxAmp;
                        leftWaves.forEach(w => {
                            const wPhase = (w.phase * Math.PI) / 180;
                            const effectiveFreq = w.freq * speed;
                            xVal += (w.amp) * Math.sin(2 * Math.PI * effectiveFreq * t + wPhase);
                        });
                        xVal = xVal / leftMaxAmp;
                    }

                    if (rightWaves.length > 0) {
                        let rightMaxAmp = rightWaves.reduce((s, w) => s + w.amp, 0);
                        rightMaxAmp = rightMaxAmp === 0 ? 1 : rightMaxAmp;
                        rightWaves.forEach(w => {
                            const wPhase = (w.phase * Math.PI) / 180;
                            const effectiveFreq = w.freq * speed;
                            yVal += (w.amp) * Math.sin(2 * Math.PI * effectiveFreq * t + wPhase);
                        });
                        yVal = yVal / rightMaxAmp;
                    }

                    return { x: xVal, y: yVal };
                };

                // Helper to draw XY trace with minimal glow
                const drawXYTrace = (leftWaves: Wave[], rightWaves: Wave[], color: string, lineWidth: number = 1.5) => {
                    if (leftWaves.length === 0 && rightWaves.length === 0) return;

                    // Single pass rendering - no ghosting
                    ctx.strokeStyle = color;
                    ctx.lineWidth = lineWidth;
                    ctx.shadowBlur = 4;
                    ctx.shadowColor = color;
                    ctx.lineJoin = 'round';
                    ctx.lineCap = 'round';
                    
                    ctx.beginPath();
                    for (let i = 0; i < segments; i++) {
                        const t = time - (history * (1 - (i / segments)));
                        const { x, y } = computeXY(leftWaves, rightWaves, t);

                        const plotX = cx + (x * radiusX);
                        const plotY = cy - (y * radiusY);
                        
                        if (i === 0) ctx.moveTo(plotX, plotY);
                        else ctx.lineTo(plotX, plotY);
                    }
                    ctx.stroke();
                };

                let hasDrawnTrace = false;

                // Draw traces based on channel configuration
                if (pureLeftWaves.length > 0 && pureRightWaves.length > 0) {
                    drawXYTrace(pureLeftWaves, pureRightWaves, '#00FF00', 3);
                    hasDrawnTrace = true;
                } else if (pureLeftWaves.length > 0 || pureRightWaves.length > 0) {
                    drawXYTrace(pureLeftWaves, pureRightWaves, '#00FF00', 3);
                    hasDrawnTrace = true;
                }

                if (bothWaves.length > 0) {
                    drawXYTrace(bothWaves, bothWaves, '#10B981', 2);
                    hasDrawnTrace = true;
                }

                if (autoPanWaves.length > 0) {
                    let weightedPan = 0;
                    let totalAmp = 0;
                    for (const w of autoPanWaves) {
                        const autoPanRate = w.autoPanRate || 0;
                        const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                        const pan = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                        weightedPan += w.amp * pan;
                        totalAmp += w.amp;
                    }
                    const currentPan = totalAmp > 0 ? weightedPan / totalAmp : 0;
                    drawXYTrace(autoPanWaves, autoPanWaves, getPanColor(currentPan), 2);
                    hasDrawnTrace = true;
                }

                if (!hasDrawnTrace && oscilloscopeActiveWaves.length > 0) {
                    const mid = Math.floor(oscilloscopeActiveWaves.length / 2);
                    drawXYTrace(oscilloscopeActiveWaves.slice(0, mid), oscilloscopeActiveWaves.slice(mid), '#00FF00', 2.5);
                }

                ctx.shadowBlur = 0;
            }

            ctx.shadowBlur = 0;
        }
        
        // --- LISSAJOUS DOMAIN (2D) ---
        else if (viewMode === 'lissajous') {
            // Axes
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx, 0);
            ctx.lineTo(cx, height);
            ctx.moveTo(0, cy);
            ctx.lineTo(width, cy);
            ctx.stroke();

            ctx.beginPath();
            ctx.strokeStyle = '#10B981'; // Emerald (for mono/both)
            ctx.lineWidth = 3;
            ctx.lineJoin = 'round';
            ctx.shadowBlur = 8;
            ctx.shadowColor = ctx.strokeStyle;

            if (activeWaves.length === 0) {
                ctx.stroke();
                return;
            }

            const margin = 40;
            const radius = (Math.min(width, height) / 2) - margin;
            const segments = 1000;
            const history = 2.0;

            // Helper to interpolate between Blue (#3b82f6) and Pink (#ec4899) based on pan
            const getPanColor = (pan: number): string => {
                const t = (pan + 1) / 2;
                const r = Math.floor(59 + (236 - 59) * t);
                const g = Math.floor(130 + (72 - 130) * t);
                const b = Math.floor(246 + (153 - 246) * t);
                return `rgb(${r}, ${g}, ${b})`;
            };

            // Helper to compute Lissajous values for a set of waves
            const computeLissajous = (wavesToDraw: Wave[], t: number) => {
                if (wavesToDraw.length === 0) return { x: 0, y: 0 };
                
                const xWave = wavesToDraw[0];
                const yWaves = wavesToDraw.slice(1);
                
                const xPhase = (xWave.phase * Math.PI) / 180;
                const effectiveFreqX = xWave.freq * speed;
                const xVal = Math.sin(2 * Math.PI * effectiveFreqX * t + xPhase);
                
                let yVal = 0;
                if (yWaves.length > 0) {
                    let yMaxAmp = yWaves.reduce((s, w) => s + w.amp, 0);
                    yMaxAmp = yMaxAmp === 0 ? 1 : yMaxAmp;
                    yWaves.forEach(w => {
                        const wPhase = (w.phase * Math.PI) / 180;
                        const effectiveFreq = w.freq * speed;
                        yVal += (w.amp) * Math.sin(2 * Math.PI * effectiveFreq * t + wPhase);
                    });
                    yVal = yVal / yMaxAmp;
                }
                
                return { x: xVal, y: yVal };
            };

            // Helper to draw a Lissajous trace
            const drawLissajousTrace = (wavesToDraw: Wave[], color: string) => {
                if (wavesToDraw.length === 0) return;
                
                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.lineWidth = 2.5;
                ctx.lineJoin = 'round';
                ctx.shadowBlur = 8;
                ctx.shadowColor = color;

                for (let i = 0; i < segments; i++) {
                    const t = time - (history * (1 - (i / segments)));
                    const { x, y } = computeLissajous(wavesToDraw, t);
                    
                    const plotX = cx + (x * radius);
                    const plotY = cy - (y * radius);

                    if (i === 0) ctx.moveTo(plotX, plotY);
                    else ctx.lineTo(plotX, plotY);
                }
                ctx.stroke();
            };

            // Separate waves into categories
            const pureLeftWaves = activeWaves.filter(w => w.channel === 'left');
            const pureRightWaves = activeWaves.filter(w => w.channel === 'right');
            const bothWaves = activeWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
            const autoPanWaves = activeWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);

            const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

            if (hasDistinctChannels || autoPanWaves.length > 0) {
                // STEREO MODE: Draw separate traces for each channel type

                // Draw left channel (blue)
                if (pureLeftWaves.length > 0) {
                    drawLissajousTrace(pureLeftWaves, '#3b82f6');
                }

                // Draw right channel (pink)
                if (pureRightWaves.length > 0) {
                    drawLissajousTrace(pureRightWaves, '#ec4899');
                }

                // Draw both/center waves (emerald)
                if (bothWaves.length > 0) {
                    drawLissajousTrace(bothWaves, '#10B981');
                }

                // Draw auto-pan waves with dynamic color
                if (autoPanWaves.length > 0) {
                    let weightedPan = 0;
                    let totalAmp = 0;
                    for (const w of autoPanWaves) {
                        const autoPanRate = w.autoPanRate || 0;
                        const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                        const pan = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                        weightedPan += w.amp * pan;
                        totalAmp += w.amp;
                    }
                    const currentPan = totalAmp > 0 ? weightedPan / totalAmp : 0;
                    drawLissajousTrace(autoPanWaves, getPanColor(currentPan));
                }

                // Add channel labels if we have distinct channels
                if (hasDistinctChannels) {
                    ctx.shadowBlur = 0;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
                    ctx.font = '11px sans-serif';
                    if (pureLeftWaves.length > 0) ctx.fillText('L', 8, cy - 10);
                    if (pureRightWaves.length > 0) ctx.fillText('R', 8, cy + 15);
                }

            } else {
                // MONO MODE: Single emerald trace
                drawLissajousTrace(activeWaves, '#10B981');
            }
            ctx.shadowBlur = 0;
        }

        // --- 3D LISSAJOUS (XYZ) ---
        else if (viewMode === 'xyz') {
            // Camera Setup
            const cameraZ = 1200 / Math.max(0.1, zoom);
            const scale = Math.min(width, height) * 0.4;
            
            // Auto Rotation Logic
            if (isAutoRotating) {
                autoRotRef.current += deltaTime * 0.2;
            }

            // Rotation controlled by mouse pan + auto rotate
            const rotX = (pan.y * 0.01) + 0.5;
            const rotY = (pan.x * 0.01) + autoRotRef.current;

            // Draw 3D Bounding Box (Reference Cube)
            const boxSize = scale * 1.2;
            const boxPoints = [
                {x:-1,y:-1,z:-1}, {x:1,y:-1,z:-1}, {x:1,y:1,z:-1}, {x:-1,y:1,z:-1}, // Back Face
                {x:-1,y:-1,z:1}, {x:1,y:-1,z:1}, {x:1,y:1,z:1}, {x:-1,y:1,z:1}      // Front Face
            ];

            const project = (x: number, y: number, z: number) => {
                 // Rotate Y
                 const rx = x * Math.cos(rotY) - z * Math.sin(rotY);
                 let rz = x * Math.sin(rotY) + z * Math.cos(rotY);
                 // Rotate X
                 const ry = y * Math.cos(rotX) - rz * Math.sin(rotX);
                 rz = y * Math.sin(rotX) + rz * Math.cos(rotX);

                 const depth = rz + cameraZ;
                 if (depth < 10) return null; // Clip
                 const screenX = cx + (rx / depth) * 1000; // 1000 is generic fov factor
                 const screenY = cy + (ry / depth) * 1000;
                 return { x: screenX, y: screenY };
            };

            // Draw Box Edges
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
            ctx.lineWidth = 1;
            const edges = [
                [0,1], [1,2], [2,3], [3,0], // Back
                [4,5], [5,6], [6,7], [7,4], // Front
                [0,4], [1,5], [2,6], [3,7]  // Connectors
            ];
            
            ctx.beginPath();
            edges.forEach(([s, e]) => {
                const start = boxPoints[s];
                const end = boxPoints[e];
                const p1 = project(start.x * boxSize, start.y * boxSize, start.z * boxSize);
                const p2 = project(end.x * boxSize, end.y * boxSize, end.z * boxSize);
                if (p1 && p2) {
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);
                }
            });
            ctx.stroke();

            // Helper to compute composite wave value for a channel set
            const computeWaveValue = (waves: Wave[], t: number) => {
                if (waves.length === 0) return 0;
                let sum = 0;
                let totalAmp = 0;
                for (const w of waves) {
                    const phase = (w.phase * Math.PI) / 180;
                    const effectiveFreq = w.freq * speed;
                    sum += w.amp * Math.sin(effectiveFreq * t * 0.2 + phase);
                    totalAmp += w.amp;
                }
                return totalAmp > 0 ? sum / totalAmp : 0;
            };

            const points = 800;
            const cycles = 4;

            ctx.lineWidth = 4;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.shadowBlur = 15;

            // Helper to interpolate between Blue (#3b82f6) and Pink (#ec4899) based on pan
            const getPanColor = (pan: number): string => {
                const t = (pan + 1) / 2;
                const r = Math.floor(59 + (236 - 59) * t);
                const g = Math.floor(130 + (72 - 130) * t);
                const b = Math.floor(246 + (153 - 246) * t);
                return `rgb(${r}, ${g}, ${b})`;
            };

            // Helper to draw a 3D trace for given waves
            const draw3DTrace = (wavesToDraw: Wave[], color: string) => {
                if (wavesToDraw.length === 0) return;
                
                ctx.strokeStyle = color;
                ctx.shadowColor = color;
                ctx.beginPath();
                for (let i = 0; i < points; i++) {
                    const tOffset = (i / points) * (Math.PI * 2 * cycles);
                    const t = tOffset + (time * 0.5);

                    const x = computeWaveValue(wavesToDraw, t);
                    const y = computeWaveValue(wavesToDraw, t + Math.PI / 2);
                    const z = computeWaveValue(wavesToDraw, t + Math.PI);

                    const p = project(x * scale, y * scale, z * scale);
                    if (p) {
                        if (i === 0) ctx.moveTo(p.x, p.y);
                        else ctx.lineTo(p.x, p.y);
                    }
                }
                ctx.stroke();
            };

            // Separate waves into categories
            const pureLeftWaves = activeWaves.filter(w => w.channel === 'left');
            const pureRightWaves = activeWaves.filter(w => w.channel === 'right');
            const bothWaves = activeWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
            const autoPanWaves = activeWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);

            const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

            if (hasDistinctChannels || autoPanWaves.length > 0) {
                // STEREO/HYBRID MODE: Draw separate traces for each channel type

                // Draw left channel (blue)
                if (pureLeftWaves.length > 0) {
                    draw3DTrace(pureLeftWaves, '#3b82f6');
                }

                // Draw right channel (pink)
                if (pureRightWaves.length > 0) {
                    draw3DTrace(pureRightWaves, '#ec4899');
                }

                // Draw both/center waves (emerald)
                if (bothWaves.length > 0) {
                    draw3DTrace(bothWaves, '#10B981');
                }

                // Draw auto-pan waves with dynamic color
                if (autoPanWaves.length > 0) {
                    let weightedPan = 0;
                    let totalAmp = 0;
                    for (const w of autoPanWaves) {
                        const autoPanRate = w.autoPanRate || 0;
                        const autoPanDepth = (w.autoPanDepth ?? 0) / 100;
                        const pan = Math.sin(2 * Math.PI * autoPanRate * time) * autoPanDepth;
                        weightedPan += w.amp * pan;
                        totalAmp += w.amp;
                    }
                    const currentPan = totalAmp > 0 ? weightedPan / totalAmp : 0;
                    draw3DTrace(autoPanWaves, getPanColor(currentPan));
                }
            } else {
                // MONO MODE: Single emerald trace
                draw3DTrace(activeWaves, '#10B981');
            }

            ctx.shadowBlur = 0;
        }

        // --- CHLADNI / CYMATICS DOMAIN ---
        else if (viewMode === 'chladni') {
            const imgData = ctx.createImageData(width, height);
            const data = imgData.data;
            
            let totalAmp = activeWaves.reduce((sum, w) => sum + w.amp, 0) * 2;
            totalAmp = totalAmp === 0 ? 1 : totalAmp;
            const invTotalAmp = 1 / totalAmp;

            const baseScale = height / 2;
            const currentScale = baseScale * zoom;
            const invScale = 1 / currentScale;

            // Pre-compute wave parameters with trigonometric values
            const waveParams = activeWaves.map(w => {
                const effectiveFreq = w.freq * speed;
                const angleBase = (w.phase * Math.PI / 180) - (time * effectiveFreq * 5);
                return {
                    amp: w.amp,
                    spatialFreq: effectiveFreq * 2.0 * Math.PI,
                    cosAngle: Math.cos(angleBase),
                    sinAngle: Math.sin(angleBase)
                };
            });

            // Pre-calculate color coefficients
            const colorR = 245, colorG = 208, colorB = 130;
            const baseR = 10, baseG = 12, baseB = 20;

            for (let y = 0; y < height; y++) {
                const ny = (y - cy - pan.y) * invScale;
                let index = y * width * 4;
                
                for (let x = 0; x < width; x++) {
                    const nx = (x - cx - pan.x) * invScale;

                    let reSum = 0;
                    let imSum = 0;

                    // Inner loop optimization: minimize operations
                    for (let i = 0; i < waveParams.length; i++) {
                        const w = waveParams[i];
                        const spatialVal = Math.cos(w.spatialFreq * nx) + Math.cos(w.spatialFreq * ny);
                        const ampSpatial = w.amp * spatialVal;
                        reSum += ampSpatial * w.cosAngle;
                        imSum += ampSpatial * w.sinAngle;
                    }

                    // Fast magnitude calculation
                    const magSquared = reSum * reSum + imSum * imSum;
                    const magnitude = Math.sqrt(magSquared);
                    const normalizedAmp = magnitude * invTotalAmp;
                    
                    // Clamp and apply power curve in one step
                    const intensity = Math.pow(Math.min(1, normalizedAmp), 1.5);

                    // Direct color assignment without intermediate calculations
                    data[index++] = baseR + (colorR * intensity);
                    data[index++] = baseG + (colorG * intensity);
                    data[index++] = baseB + (colorB * intensity);
                    data[index++] = 255;
                }
            }
            ctx.putImageData(imgData, 0, 0);
        }

        // --- WATER RIPPLE DOMAIN ---
        else if (viewMode === 'water') {
            const imgData = ctx.createImageData(width, height);
            const data = imgData.data;

            const poolRadius = (Math.min(width, height) / 2) * 0.85 * zoom;
            const centerX = cx + pan.x;
            const centerY = cy + pan.y;

            // Pre-calculate wave properties
            const waveParams = activeWaves.map(w => {
                const effectiveFreq = w.freq * speed; // Time dilation
                return {
                    amp: w.amp,
                    // Wave number k (spatial frequency)
                    k: effectiveFreq * 0.5, 
                    // Speed needs to be proportional to freq for non-dispersive appearance, 
                    // or just constant for water look. Let's use constant speed assumption for simplicity:
                    // phase = k*r - w*t. 
                    speedFactor: effectiveFreq * 8, 
                    phaseOffset: (w.phase * Math.PI) / 180
                };
            });

            for (let y = 0; y < height; y++) {
                for (let x = 0; x < width; x++) {
                    const dx = x - centerX;
                    const dy = y - centerY;
                    const dist = Math.sqrt(dx*dx + dy*dy);
                    const idx = (y * width + x) * 4;

                    if (dist > poolRadius) {
                        // Outside pool - simple dark background
                        data[idx] = 17; data[idx+1] = 24; data[idx+2] = 39; data[idx+3] = 255;
                        continue;
                    }

                    const rNorm = dist / poolRadius; // 0 to 1 inside pool
                    
                    let h = 0;
                    let gradX = 0;
                    let gradY = 0;

                    for (const w of waveParams) {
                        // 1. Outward Wave: sin(k*r - w*t)
                        // Note: dist here is in pixels, we scale k relative to pool size for aesthetics
                        // Let's use normalized radius for calculation to make "frequency" relative to pool size
                        const rVal = rNorm * 20; // 20 units across radius
                        
                        // Outward Phase
                        const argOut = (w.k * rVal) - (time * w.speedFactor) + w.phaseOffset;
                        const wOut = Math.sin(argOut);

                        // 2. Inward (Reflected) Wave: 
                        // Travels distance 2R (center->edge->center). At 'r', it has traveled R + (R-r) = 2R - r.
                        // We subtract amplitude to simulate hard boundary reflection (180 deg phase shift)
                        const rRefl = (2 - rNorm) * 20; 
                        const argIn = (w.k * rRefl) - (time * w.speedFactor) + w.phaseOffset;
                        const wIn = Math.sin(argIn);

                        // Combine with damping on reflection
                        const damping = 0.7;
                        const waveH = w.amp * (wOut - (wIn * damping));
                        h += waveH;

                        // Calculate gradient (derivative) for lighting
                        // d(sin(u))/dx = cos(u) * du/dx.  du/dr = k. dr/dx = x/r.
                        // dH/dr
                        const dOut = w.k * Math.cos(argOut);
                        const dIn = w.k * Math.cos(argIn) * -1 * damping; // chain rule (2-r) -> -1
                        
                        const dHdr = w.amp * (dOut - dIn);
                        
                        // Avoid division by zero at center
                        const safeDist = dist < 1 ? 1 : dist;
                        gradX += dHdr * (dx / safeDist);
                        gradY += dHdr * (dy / safeDist);
                    }

                    // Revised Lighting Model for clarity
                    const slopeScale = 0.02; // Lowered to reduce noise/shimmer
                    const sx = gradX * slopeScale;
                    const sy = gradY * slopeScale;
                    
                    // Light direction (top-left)
                    const lx = -0.6;
                    const ly = -0.6;
                    
                    // Diffuse component (Fake N dot L)
                    // Measures how much the wave slope faces the light source
                    const diffuse = (sx * lx + sy * ly);
                    
                    // Base shading from height (crests lighter, troughs darker)
                    const heightShade = h * 1.2;

                    // Specular Highlight
                    // Sharper exponent (16) reduces widespread shimmer, keeps it to peaks
                    const specular = Math.pow(Math.max(0, diffuse - 0.2), 16) * 60;
                    
                    // Light intensity mixing
                    const lightIntensity = (diffuse * 60);

                    // Color Composition
                    // R: Mostly specular highlight + faint height tint
                    data[idx]   = Math.max(0, Math.min(255, 0 + specular + heightShade * 0.5));
                    // G: Teal/Green tint
                    data[idx+1] = Math.max(0, Math.min(255, 50 + lightIntensity + specular + heightShade));
                    // B: Strong Blue base
                    data[idx+2] = Math.max(0, Math.min(255, 110 + lightIntensity + specular + heightShade));
                    data[idx+3] = 255;
                }
            }
            ctx.putImageData(imgData, 0, 0);

            // Draw vector border
            ctx.beginPath();
            ctx.arc(centerX, centerY, poolRadius, 0, Math.PI * 2);
            ctx.strokeStyle = '#60a5fa'; // Blue-400
            ctx.lineWidth = 4;
            ctx.stroke();
            ctx.strokeStyle = 'rgba(255,255,255,0.2)';
            ctx.lineWidth = 1;
            ctx.stroke();
        }

        // --- NON-NEWTONIAN FLUID (3D MESH) ---
        else if (viewMode === 'fluid') {
            // 3D Projection Parameters
            const gridSize = 63; // Resolution of the mesh (Increased for 3x3 scaling)
            // Scale out to 3x3 area (width * 4.0 covers roughly 3x the previous area)
            const gridSpacing = (width * 4.0) / gridSize; 
            const offset = (gridSize * gridSpacing) / 2;
            
            const cameraZ = 2500 / Math.max(0.1, zoom); 
            const screenScale = 1800; 

            // Auto Rotation Logic (same pattern as XYZ)
            if (isAutoRotating) {
                autoRotRef.current += deltaTime * 0.2;
            }

            // Rotation controlled by mouse pan + auto rotate
            const rotX = 0.8 + (pan.y * 0.005); // Tilt
            const rotY = (pan.x * 0.005) + autoRotRef.current; // Spin

            // Pre-calculate wave data
            const waveParams = activeWaves.map(w => ({
                amp: w.amp,
                spatialFreq: (w.freq * Math.PI) / (gridSize / 2),
                angleBase: (w.phase * Math.PI / 180) - (time * w.freq * 8)
            }));

            // Calculate Grid Points
            const points: {x: number, y: number, z: number, depth: number}[] = [];

            for (let z = 0; z < gridSize; z++) {
                for (let x = 0; x < gridSize; x++) {
                    // Physical Coordinates centered at 0
                    const px = (x * gridSpacing) - offset;
                    const pz = (z * gridSpacing) - offset;

                    // Calculate Height (Interference)
                    let h = 0;
                    if (activeWaves.length > 0) {
                        let reSum = 0;
                        let imSum = 0;
                        
                        // Scale normalized coords to -3 to 3 to show 3x3 repetition pattern
                        const nx = ((x / gridSize) * 6) - 3;
                        const nz = ((z / gridSize) * 6) - 3;

                        for (const w of waveParams) {
                            const d = Math.sqrt(nx*nx + nz*nz);
                            const spatialVal = Math.cos(w.spatialFreq * d * 5) + Math.cos(w.spatialFreq * nx * 2);

                            reSum += w.amp * spatialVal * Math.cos(w.angleBase);
                            imSum += w.amp * spatialVal * Math.sin(w.angleBase);
                        }
                        
                        const mag = Math.sqrt(reSum * reSum + imSum * imSum);
                        const threshold = 15; 
                        if (mag > threshold) {
                            // Reduced power and multiplier to prevent spikes from stretching to infinity
                            h = -Math.pow((mag - threshold), 1.2) * 1.5;
                        } else {
                            h = -Math.sin(mag) * 2; 
                        }
                    }

                    // 3D Transformation
                    
                    // 1. Rotation (World Space)
                    // Rotate Y
                    const rx = px * Math.cos(rotY) - pz * Math.sin(rotY);
                    let rz = px * Math.sin(rotY) + pz * Math.cos(rotY);
                    
                    // Rotate X (Tilt)
                    const ry = h * Math.cos(rotX) - rz * Math.sin(rotX);
                    rz = h * Math.sin(rotX) + rz * Math.cos(rotX);

                    // 2. Camera Translation
                    const Z = rz + cameraZ;

                    // 3. Perspective Projection: X' = X / Z
                    if (Z <= 100) continue; // Near clip plane

                    const xPrime = rx / Z;
                    const yPrime = ry / Z;

                    // 4. Viewport Mapping (Screen Space)
                    const sx = xPrime * screenScale + cx;
                    const sy = yPrime * screenScale + cy + (pan.y * 0.5);

                    const depthMetric = screenScale / Z;

                    points.push({ x: sx, y: sy, z: rz, depth: depthMetric });
                }
            }

            // Draw Mesh (Painter's Algorithm by loop order Z then X)
            ctx.lineWidth = 1;
            
            for (let z = 0; z < gridSize - 1; z++) {
                for (let x = 0; x < gridSize - 1; x++) {
                    const i = z * gridSize + x;
                    const p1 = points[i];
                    const p2 = points[i + 1];
                    const p3 = points[i + gridSize + 1];
                    const p4 = points[i + gridSize];

                    if (!p1 || !p2 || !p3 || !p4) continue;

                    ctx.beginPath();
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);
                    ctx.lineTo(p3.x, p3.y);
                    ctx.lineTo(p4.x, p4.y);
                    ctx.closePath();

                    // Fluid Color Grading
                    const heightFactor = Math.min(1, Math.abs(p1.y - cy) / 150);
                    
                    const r = 40 + (heightFactor * 100);
                    const g = 10 + (heightFactor * 200);
                    const b = 60 + (heightFactor * 255);
                    const alpha = 0.4 + (p1.depth * 0.6);

                    ctx.fillStyle = `rgba(${r},${g},${b},${alpha * 0.8})`;
                    ctx.strokeStyle = `rgba(${r+50},${g+50},${b+100},${alpha * 0.3})`;
                    
                    ctx.fill();
                    ctx.stroke();
                }
            }
        }

        // --- GALAXY SPIRAL VISUALIZATION ---
        else if (viewMode === 'galaxy') {
            // Deep space background with stars
            ctx.fillStyle = '#000510';
            ctx.fillRect(0, 0, width, height);
            
            // Auto Rotation Logic (with inertia when music is playing)
            if (isAutoRotating) {
                const rotationSpeed = 0.15;
                
                // Add music-reactive rotation variation (only when music playing)
                if (smoothedMusicData && isUploadedAudioPlaying) {
                    const energyInfluence = (smoothedMusicData.totalEnergy / 500) * 0.3;
                    const targetVelocity = rotationSpeed + energyInfluence;
                    
                    // Smooth interpolation with inertia
                    rotationVelocityRef.current = rotationVelocityRef.current * 0.85 + targetVelocity * 0.15;
                    autoRotRef.current += deltaTime * rotationVelocityRef.current;
                } else {
                    autoRotRef.current += deltaTime * rotationSpeed;
                    rotationVelocityRef.current = rotationSpeed;
                }
            }

            // Rotation angle from interaction and auto-rotation
            const galaxyRotation = (pan.x * 0.003) + autoRotRef.current;
            
            // Calculate number of spiral arms based on active waves
            const numArms = Math.max(2, Math.min(8, activeWaves.length));
            
            // Calculate total energy and frequency distribution
            // Use smoothed music data when available, otherwise fall back to waves
            const totalEnergy = smoothedMusicData 
                ? smoothedMusicData.totalEnergy / 10
                : activeWaves.reduce((sum, w) => sum + w.amp, 0);
            const avgFreq = smoothedMusicData
                ? (smoothedMusicData.bassEnergy * 0.5 + smoothedMusicData.midEnergy * 2 + smoothedMusicData.trebleEnergy * 5) / (smoothedMusicData.totalEnergy || 1)
                : (activeWaves.length > 0 ? activeWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);
            
            // Spiral parameters influenced by frequencies
            const spiralTightness = 0.15 + (avgFreq * 0.02); // Higher freq = tighter spiral
            const particlesPerArm = 300;
            const maxRadius = Math.min(width, height) * 0.45 * zoom;
            
            // Core rendering - Supermassive black hole effect
            const coreRadius = 15 + (totalEnergy * 0.3);
            const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius * 3);
            gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
            gradient.addColorStop(0.1, 'rgba(180, 120, 255, 0.9)');
            gradient.addColorStop(0.3, 'rgba(100, 50, 255, 0.6)');
            gradient.addColorStop(0.6, 'rgba(50, 20, 150, 0.3)');
            gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
            
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(cx, cy, coreRadius * 3, 0, Math.PI * 2);
            ctx.fill();
            
            // Accretion disk
            ctx.strokeStyle = 'rgba(255, 200, 100, 0.4)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(cx, cy, coreRadius * 2, 0, Math.PI * 2);
            ctx.stroke();
            
            // Helper to get color for each wave/arm
            const getArmColor = (waveIndex: number, distance: number, waveAmp: number) => {
                const wave = activeWaves[waveIndex % activeWaves.length];
                const baseColor = wave.color;
                
                // Parse hex color
                const r = parseInt(baseColor.slice(1, 3), 16);
                const g = parseInt(baseColor.slice(3, 5), 16);
                const b = parseInt(baseColor.slice(5, 7), 16);
                
                // Add depth fade and energy glow
                const distanceFade = Math.pow(1 - (distance / maxRadius), 1.5);
                const energyGlow = Math.min(1, waveAmp / 100);
                const alpha = distanceFade * 0.8 * energyGlow;
                
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            
            // Draw spiral arms with particles
            activeWaves.forEach((wave, armIndex) => {
                const armAngleOffset = (armIndex * 2 * Math.PI) / numArms;
                const wavePhaseOffset = (wave.phase * Math.PI) / 180;
                const effectiveFreq = wave.freq * speed;
                
                // Create particles along spiral arm
                for (let i = 0; i < particlesPerArm; i++) {
                    const t = i / particlesPerArm;
                    const radius = maxRadius * Math.pow(t, 0.8); // Non-linear expansion
                    
                    // Logarithmic spiral equation: r = a * e^(b*θ)
                    // Simplified: θ = spiralTightness * radius + rotation
                    const spiralAngle = spiralTightness * radius + armAngleOffset + galaxyRotation;
                    
                    // Add wave motion to spiral
                    const waveMotion = Math.sin(effectiveFreq * time * 0.5 + wavePhaseOffset + t * Math.PI * 4) * 0.1;
                    const finalAngle = spiralAngle + waveMotion;
                    
                    // Position
                    const x = cx + Math.cos(finalAngle) * radius;
                    const y = cy + Math.sin(finalAngle) * radius;
                    
                    // Particle size varies with amplitude and distance
                    const baseSize = 2 + (wave.amp * 0.03);
                    const distanceFactor = Math.pow(1 - t, 0.5);
                    const size = baseSize * distanceFactor;
                    
                    // Pulsating effect synchronized with frequency
                    const pulse = 1 + Math.sin(effectiveFreq * time * 2 + t * Math.PI * 8) * 0.3;
                    const finalSize = size * pulse;
                    
                    // Color with depth
                    const color = getArmColor(armIndex, radius, wave.amp);
                    
                    // Draw particle with glow
                    ctx.shadowBlur = 8 + (wave.amp * 0.1);
                    ctx.shadowColor = wave.color;
                    ctx.fillStyle = color;
                    ctx.beginPath();
                    ctx.arc(x, y, finalSize, 0, Math.PI * 2);
                    ctx.fill();
                    
                    // Add occasional bright stars
                    if (i % 15 === 0 && t > 0.3) {
                        ctx.fillStyle = `rgba(255, 255, 255, ${0.6 * (1 - t)})`;
                        ctx.beginPath();
                        ctx.arc(x, y, finalSize * 1.5, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
                
                // Draw connecting curves between particles (nebula effect)
                ctx.shadowBlur = 15;
                ctx.shadowColor = wave.color;
                ctx.strokeStyle = getArmColor(armIndex, 0, wave.amp * 0.5);
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                
                for (let i = 0; i < particlesPerArm; i++) {
                    const t = i / particlesPerArm;
                    const radius = maxRadius * Math.pow(t, 0.8);
                    const spiralAngle = spiralTightness * radius + armAngleOffset + galaxyRotation;
                    const waveMotion = Math.sin(effectiveFreq * time * 0.5 + wavePhaseOffset + t * Math.PI * 4) * 0.1;
                    const finalAngle = spiralAngle + waveMotion;
                    
                    const x = cx + Math.cos(finalAngle) * radius;
                    const y = cy + Math.sin(finalAngle) * radius;
                    
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
            });
            
            // Add distant stars in background
            ctx.shadowBlur = 0;
            const numStars = 200;
            const starSeed = 12345; // Consistent stars
            for (let i = 0; i < numStars; i++) {
                // Pseudo-random but deterministic positions
                const x = ((starSeed * i * 7919) % width);
                const y = ((starSeed * i * 7727) % height);
                const twinkle = Math.sin(time * 2 + i * 0.1) * 0.5 + 0.5;
                const size = 0.5 + (i % 3) * 0.3;
                
                ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + twinkle * 0.4})`;
                ctx.beginPath();
                ctx.arc(x, y, size, 0, Math.PI * 2);
                ctx.fill();
            }
            
            // Reset shadow
            ctx.shadowBlur = 0;
            
            // Draw velocity vectors (optional enhancement)
            if (zoom > 0.7) {
                activeWaves.forEach((wave, armIndex) => {
                    const armAngleOffset = (armIndex * 2 * Math.PI) / numArms;
                    const sampleRadius = maxRadius * 0.6;
                    const spiralAngle = spiralTightness * sampleRadius + armAngleOffset + galaxyRotation;
                    
                    const x = cx + Math.cos(spiralAngle) * sampleRadius;
                    const y = cy + Math.sin(spiralAngle) * sampleRadius;
                    
                    // Velocity direction (tangent to spiral)
                    const tangentAngle = spiralAngle + Math.PI / 2;
                    const velocityLength = 20 + (wave.amp * 0.2);
                    const vx = Math.cos(tangentAngle) * velocityLength;
                    const vy = Math.sin(tangentAngle) * velocityLength;
                    
                    ctx.strokeStyle = `rgba(100, 200, 255, 0.3)`;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x + vx, y + vy);
                    ctx.stroke();
                    
                    // Arrowhead
                    const arrowSize = 4;
                    ctx.fillStyle = `rgba(100, 200, 255, 0.3)`;
                    ctx.beginPath();
                    ctx.moveTo(x + vx, y + vy);
                    ctx.lineTo(x + vx - arrowSize * Math.cos(tangentAngle - 0.3), 
                              y + vy - arrowSize * Math.sin(tangentAngle - 0.3));
                    ctx.lineTo(x + vx - arrowSize * Math.cos(tangentAngle + 0.3), 
                              y + vy - arrowSize * Math.sin(tangentAngle + 0.3));
                    ctx.closePath();
                    ctx.fill();
                });
            }
        }

        // --- SACRED GEOMETRY VISUALIZATION ---
        else if (viewMode === 'sacred') {
            // Deep mystical background
            const bgGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height));
            bgGradient.addColorStop(0, '#0a0520');
            bgGradient.addColorStop(0.5, '#050212');
            bgGradient.addColorStop(1, '#000000');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, width, height);
            
            // Auto Rotation Logic (with music-reactive inertia)
            if (isAutoRotating) {
                const rotationSpeed = 0.1;
                
                if (smoothedMusicData && isUploadedAudioPlaying) {
                    // Treble-influenced rotation for sacred geometry
                    const trebleInfluence = (smoothedMusicData.trebleEnergy / 300) * 0.15;
                    const targetVelocity = rotationSpeed + trebleInfluence;
                    rotationVelocityRef.current = rotationVelocityRef.current * 0.9 + targetVelocity * 0.1;
                    autoRotRef.current += deltaTime * rotationVelocityRef.current;
                } else {
                    autoRotRef.current += deltaTime * rotationSpeed;
                    rotationVelocityRef.current = rotationSpeed;
                }
            }
            
            const rotation = autoRotRef.current + (pan.x * 0.002);
            const scale = Math.min(width, height) * 0.35 * zoom;
            
            // Calculate total energy and frequency characteristics
            // Use smoothed music data when available
            const totalEnergy = smoothedMusicData 
                ? smoothedMusicData.totalEnergy / 10
                : activeWaves.reduce((sum, w) => sum + w.amp, 0);
            const avgFreq = smoothedMusicData
                ? (smoothedMusicData.bassEnergy * 0.5 + smoothedMusicData.midEnergy * 2 + smoothedMusicData.trebleEnergy * 5) / (smoothedMusicData.totalEnergy || 1)
                : (activeWaves.length > 0 ? activeWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);
            
            // Helper: Draw circle with pulsation
            const drawCircle = (x: number, y: number, radius: number, color: string, pulsePhase: number, lineWidth: number = 2) => {
                const pulse = 1 + Math.sin(time * avgFreq + pulsePhase) * 0.1;
                ctx.strokeStyle = color;
                ctx.lineWidth = lineWidth;
                ctx.beginPath();
                ctx.arc(x, y, radius * pulse, 0, Math.PI * 2);
                ctx.stroke();
            };
            
            // Helper: Get wave color with alpha
            const getWaveColor = (waveIndex: number, alpha: number = 0.6) => {
                if (activeWaves.length === 0) return `rgba(255, 215, 0, ${alpha})`;
                const wave = activeWaves[waveIndex % activeWaves.length];
                const r = parseInt(wave.color.slice(1, 3), 16);
                const g = parseInt(wave.color.slice(3, 5), 16);
                const b = parseInt(wave.color.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            
            // Save context for transformations
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rotation);
            
            // === FLOWER OF LIFE === (Foundation pattern)
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'rgba(255, 215, 0, 0.5)';
            
            const flowerRadius = scale * 0.5;
            const circlePositions: {x: number, y: number}[] = [{x: 0, y: 0}];
            
            // Create 6 circles around center (first ring)
            for (let i = 0; i < 6; i++) {
                const angle = (i * Math.PI * 2) / 6;
                const x = Math.cos(angle) * flowerRadius;
                const y = Math.sin(angle) * flowerRadius;
                circlePositions.push({x, y});
                
                const pulsePhase = i * Math.PI / 3;
                drawCircle(x, y, flowerRadius, getWaveColor(i, 0.4), pulsePhase, 1.5);
            }
            
            // Center circle
            drawCircle(0, 0, flowerRadius, getWaveColor(0, 0.5), 0, 2);
            
            // Second ring (outer flower petals)
            for (let i = 0; i < 6; i++) {
                const angle = (i * Math.PI * 2) / 6;
                const x = Math.cos(angle) * flowerRadius * 2;
                const y = Math.sin(angle) * flowerRadius * 2;
                const pulsePhase = i * Math.PI / 3 + Math.PI;
                drawCircle(x, y, flowerRadius, getWaveColor(i, 0.25), pulsePhase, 1);
            }
            
            // === VESICA PISCIS === (Intersection of two circles)
            ctx.shadowBlur = 20;
            ctx.shadowColor = 'rgba(138, 43, 226, 0.6)';
            
            const vesicaRadius = scale * 0.4;
            const vesicaOffset = vesicaRadius * 0.5;
            
            ctx.strokeStyle = getWaveColor(0, 0.6);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(-vesicaOffset, 0, vesicaRadius, 0, Math.PI * 2);
            ctx.stroke();
            
            ctx.strokeStyle = getWaveColor(1, 0.6);
            ctx.beginPath();
            ctx.arc(vesicaOffset, 0, vesicaRadius, 0, Math.PI * 2);
            ctx.stroke();
            
            // Fill the vesica piscis (sacred almond shape)
            ctx.fillStyle = getWaveColor(2, 0.1);
            ctx.beginPath();
            ctx.arc(-vesicaOffset, 0, vesicaRadius, -Math.asin(vesicaOffset/vesicaRadius), Math.asin(vesicaOffset/vesicaRadius));
            ctx.arc(vesicaOffset, 0, vesicaRadius, Math.PI - Math.asin(vesicaOffset/vesicaRadius), Math.PI + Math.asin(vesicaOffset/vesicaRadius));
            ctx.closePath();
            ctx.fill();
            
            // === METATRON'S CUBE === (13 circles connected)
            ctx.shadowBlur = 10;
            const metatronRadius = scale * 0.2;
            const metatronPositions: {x: number, y: number}[] = [{x: 0, y: 0}];
            
            // Inner hexagon
            for (let i = 0; i < 6; i++) {
                const angle = (i * Math.PI * 2) / 6;
                metatronPositions.push({
                    x: Math.cos(angle) * metatronRadius,
                    y: Math.sin(angle) * metatronRadius
                });
            }
            
            // Outer hexagon
            for (let i = 0; i < 6; i++) {
                const angle = (i * Math.PI * 2) / 6 + Math.PI / 6;
                metatronPositions.push({
                    x: Math.cos(angle) * metatronRadius * 1.8,
                    y: Math.sin(angle) * metatronRadius * 1.8
                });
            }
            
            // Draw Metatron's connections (platonic solid edges)
            ctx.strokeStyle = getWaveColor(3, 0.3);
            ctx.lineWidth = 1;
            for (let i = 0; i < metatronPositions.length; i++) {
                for (let j = i + 1; j < metatronPositions.length; j++) {
                    const p1 = metatronPositions[i];
                    const p2 = metatronPositions[j];
                    const dist = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
                    
                    // Only draw certain connections to reveal platonic solids
                    if (dist < metatronRadius * 2.5) {
                        ctx.beginPath();
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            }
            
            // Draw Metatron's circles
            metatronPositions.forEach((pos, i) => {
                const circleRadius = metatronRadius * 0.15;
                const pulsePhase = i * Math.PI / 6;
                const pulse = 1 + Math.sin(time * avgFreq * 2 + pulsePhase) * 0.2;
                
                ctx.shadowColor = getWaveColor(i, 0.8);
                ctx.fillStyle = getWaveColor(i, 0.4);
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, circleRadius * pulse, 0, Math.PI * 2);
                ctx.fill();
            });
            
            // === GOLDEN RATIO SPIRAL === (Fibonacci spiral)
            ctx.shadowBlur = 12;
            ctx.shadowColor = 'rgba(255, 215, 0, 0.6)';
            
            activeWaves.forEach((wave, waveIndex) => {
                const effectiveFreq = wave.freq * speed;
                const phaseOffset = (wave.phase * Math.PI) / 180;
                const spiralScale = scale * 0.6 * (wave.amp / 100);
                
                ctx.strokeStyle = getWaveColor(waveIndex, 0.5);
                ctx.lineWidth = 2;
                ctx.beginPath();
                
                const goldenRatio = 1.618033988749;
                const numPoints = 120;
                
                for (let i = 0; i < numPoints; i++) {
                    const t = i / numPoints;
                    const angle = t * Math.PI * 6 + (time * effectiveFreq * 0.5) + phaseOffset;
                    const radius = spiralScale * Math.pow(goldenRatio, t * 4 - 2);
                    
                    const x = Math.cos(angle) * radius;
                    const y = Math.sin(angle) * radius;
                    
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
            });
            
            // === SRI YANTRA === (Sacred Hindu/Buddhist mandala)
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'rgba(255, 20, 147, 0.5)';
            
            const yantराRadius = scale * 0.8;
            
            // Draw 9 interlocking triangles (4 pointing up, 5 pointing down)
            const triangleConfigs = [
                // Upward triangles (masculine/Shiva)
                { scale: 0.9, rotation: 0, color: getWaveColor(0, 0.35), pointing: 'up' },
                { scale: 0.7, rotation: Math.PI / 9, color: getWaveColor(1, 0.35), pointing: 'up' },
                { scale: 0.5, rotation: Math.PI / 6, color: getWaveColor(2, 0.35), pointing: 'up' },
                { scale: 0.3, rotation: Math.PI / 4, color: getWaveColor(3, 0.35), pointing: 'up' },
                // Downward triangles (feminine/Shakti)
                { scale: 0.85, rotation: Math.PI, color: getWaveColor(4, 0.35), pointing: 'down' },
                { scale: 0.65, rotation: Math.PI + Math.PI / 9, color: getWaveColor(5, 0.35), pointing: 'down' },
                { scale: 0.45, rotation: Math.PI + Math.PI / 6, color: getWaveColor(6, 0.35), pointing: 'down' },
                { scale: 0.25, rotation: Math.PI + Math.PI / 4, color: getWaveColor(7, 0.35), pointing: 'down' },
                { scale: 0.15, rotation: Math.PI + Math.PI / 3, color: getWaveColor(0, 0.4), pointing: 'down' }
            ];
            
            triangleConfigs.forEach((config, i) => {
                const triSize = yantराRadius * config.scale;
                const pulse = 1 + Math.sin(time * avgFreq + i * Math.PI / 4) * 0.05;
                const rotOffset = config.pointing === 'up' ? 0 : Math.PI;
                
                ctx.strokeStyle = config.color;
                ctx.fillStyle = config.color.replace(/[\d.]+\)$/g, '0.08)');
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                
                for (let j = 0; j < 3; j++) {
                    const angle = (j * 2 * Math.PI / 3) + config.rotation + rotOffset;
                    const x = Math.cos(angle) * triSize * pulse;
                    const y = Math.sin(angle) * triSize * pulse;
                    if (j === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            });
            
            // === TORUS ENERGY FIELD === (3D projection)
            ctx.shadowBlur = 20;
            ctx.shadowColor = 'rgba(0, 255, 255, 0.5)';
            
            const torusR = scale * 0.45; // Major radius
            const torusr = scale * 0.15; // Minor radius
            const torusSegments = 40;
            const torusTubes = 20;
            
            // Simple orthographic projection
            const camAngle = time * avgFreq * 0.3;
            
            for (let i = 0; i < torusSegments; i++) {
                const u = (i / torusSegments) * Math.PI * 2;
                
                for (let j = 0; j < torusTubes; j += 2) {
                    const v = (j / torusTubes) * Math.PI * 2;
                    
                    // Parametric torus equations
                    const x = (torusR + torusr * Math.cos(v)) * Math.cos(u);
                    const y = (torusR + torusr * Math.cos(v)) * Math.sin(u);
                    const z = torusr * Math.sin(v);
                    
                    // Rotate around Y axis
                    const rotX = x * Math.cos(camAngle) - z * Math.sin(camAngle);
                    const rotZ = x * Math.sin(camAngle) + z * Math.cos(camAngle);
                    
                    // Project to 2D (orthographic)
                    const screenX = rotX;
                    const screenY = y;
                    
                    // Depth-based coloring
                    const depth = (rotZ + torusR + torusr) / ((torusR + torusr) * 2);
                    const alpha = 0.15 + depth * 0.25;
                    
                    ctx.fillStyle = getWaveColor(Math.floor(i / 5) % activeWaves.length, alpha);
                    ctx.beginPath();
                    ctx.arc(screenX, screenY, 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            
            // === CENTRAL BINDU === (Sacred point/seed)
            ctx.shadowBlur = 30;
            ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
            
            const binduGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 12);
            binduGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
            binduGradient.addColorStop(0.5, 'rgba(255, 215, 0, 0.8)');
            binduGradient.addColorStop(1, 'rgba(138, 43, 226, 0.2)');
            
            const binduPulse = 1 + Math.sin(time * avgFreq * 3) * 0.3;
            ctx.fillStyle = binduGradient;
            ctx.beginPath();
            ctx.arc(0, 0, 8 * binduPulse, 0, Math.PI * 2);
            ctx.fill();
            
            // === PLATONIC SOLIDS OVERLAY === (Wireframe)
            // Dodecahedron vertices projection
            if (totalEnergy > 50) {
                ctx.shadowBlur = 8;
                ctx.strokeStyle = getWaveColor(0, 0.2);
                ctx.lineWidth = 0.5;
                
                const phi = (1 + Math.sqrt(5)) / 2; // Golden ratio
                const dodecaR = scale * 0.6;
                
                // Simplified dodecahedron vertices
                const vertices = [
                    [1, 1, 1], [-1, 1, 1], [1, -1, 1], [1, 1, -1],
                    [0, phi, 1/phi], [0, -phi, 1/phi], [0, phi, -1/phi], [0, -phi, -1/phi],
                    [1/phi, 0, phi], [-1/phi, 0, phi], [1/phi, 0, -phi], [-1/phi, 0, -phi],
                    [phi, 1/phi, 0], [-phi, 1/phi, 0], [phi, -1/phi, 0], [-phi, -1/phi, 0]
                ];
                
                const rotY = time * avgFreq * 0.2;
                const rotX = time * avgFreq * 0.15;
                
                const projectedVerts = vertices.map(([x, y, z]) => {
                    // Rotate Y
                    const rx = x * Math.cos(rotY) - z * Math.sin(rotY);
                    let rz = x * Math.sin(rotY) + z * Math.cos(rotY);
                    // Rotate X
                    const ry = y * Math.cos(rotX) - rz * Math.sin(rotX);
                    rz = y * Math.sin(rotX) + rz * Math.cos(rotX);
                    
                    return { x: rx * dodecaR * 0.3, y: ry * dodecaR * 0.3, z: rz };
                });
                
                // Draw edges between close vertices
                for (let i = 0; i < projectedVerts.length; i++) {
                    for (let j = i + 1; j < projectedVerts.length; j++) {
                        const v1 = projectedVerts[i];
                        const v2 = projectedVerts[j];
                        const dist = Math.sqrt((v2.x - v1.x) ** 2 + (v2.y - v1.y) ** 2);
                        
                        if (dist < dodecaR * 0.5) {
                            ctx.beginPath();
                            ctx.moveTo(v1.x, v1.y);
                            ctx.lineTo(v2.x, v2.y);
                            ctx.stroke();
                        }
                    }
                }
            }
            
            ctx.restore();
            ctx.shadowBlur = 0;
            
            // === SACRED MANTRAS === (Visual frequency bands)
            const numBands = Math.min(activeWaves.length, 8);
            const bandRadius = scale * 1.3;
            
            activeWaves.slice(0, numBands).forEach((wave, i) => {
                const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
                const effectiveFreq = wave.freq * speed;
                const barHeight = (wave.amp / 100) * scale * 0.3;
                const pulse = 1 + Math.sin(time * effectiveFreq * 2) * 0.2;
                
                const x1 = cx + Math.cos(angle) * bandRadius;
                const y1 = cy + Math.sin(angle) * bandRadius;
                const x2 = cx + Math.cos(angle) * (bandRadius + barHeight * pulse);
                const y2 = cy + Math.sin(angle) * (bandRadius + barHeight * pulse);
                
                const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
                gradient.addColorStop(0, getWaveColor(i, 0.2));
                gradient.addColorStop(1, getWaveColor(i, 0.8));
                
                ctx.strokeStyle = gradient;
                ctx.lineWidth = 4;
                ctx.shadowBlur = 10;
                ctx.shadowColor = getWaveColor(i, 0.6);
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
            });
            
            ctx.shadowBlur = 0;
        }

        // --- DMT HYPERSPACE VISUALIZATION ---
        else if (viewMode === 'dmt') {
            // Intense psychedelic background with color cycling
            const hueShift = (time * 50) % 360;
            const bgGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height) * 0.8);
            bgGradient.addColorStop(0, `hsl(${hueShift}, 70%, 5%)`);
            bgGradient.addColorStop(0.5, `hsl(${(hueShift + 60) % 360}, 80%, 3%)`);
            bgGradient.addColorStop(1, `hsl(${(hueShift + 120) % 360}, 90%, 1%)`);
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, width, height);
            
            // Calculate energy and frequency - Use smoothed music data when available
            const totalEnergy = smoothedMusicData 
                ? smoothedMusicData.totalEnergy / 10
                : activeWaves.reduce((sum, w) => sum + w.amp, 0);
            const avgFreq = smoothedMusicData
                ? (smoothedMusicData.bassEnergy * 0.3 + smoothedMusicData.midEnergy * 1.5 + smoothedMusicData.trebleEnergy * 3) / (smoothedMusicData.totalEnergy || 1)
                : (activeWaves.length > 0 ? activeWaves.reduce((sum, w) => sum + (w.freq * w.amp), 0) / totalEnergy : 1);
            
            const intensity = Math.min(1, totalEnergy / 300); // Trip intensity
            const hyperSpeed = avgFreq * speed * 3; // Extremely fast morphing
            
            // === CHRYSANTHEMUM BREAKTHROUGH TUNNEL ===
            ctx.save();
            ctx.translate(cx, cy);
            
            const numPetals = 8 + Math.floor(intensity * 8); // 8-16 petals
            const tunnelDepth = 15; // Layers going into the distance
            
            ctx.shadowBlur = 20;
            
            for (let depth = tunnelDepth; depth >= 0; depth--) {
                const depthRatio = depth / tunnelDepth;
                const scale = Math.pow(depthRatio, 1.5) * zoom;
                const layerRadius = Math.min(width, height) * 0.4 * scale;
                
                if (layerRadius < 5) continue;
                
                // Rotation increases with depth (creating spiral tunnel)
                const rotation = time * hyperSpeed * 0.5 + depth * 0.3 + (pan.x * 0.01);
                
                // Color shifts through depth
                const depthHue = (hueShift + depth * 25 + time * 30) % 360;
                const alpha = 0.4 + depthRatio * 0.4;
                
                for (let i = 0; i < numPetals; i++) {
                    const angle = (i * 2 * Math.PI / numPetals) + rotation;
                    
                    // Petal wave modulation
                    const waveModulation = activeWaves.reduce((sum, w, idx) => {
                        const phase = (w.phase * Math.PI / 180) + time * w.freq * speed;
                        return sum + Math.sin(phase + i * 0.5 + depth * 0.2) * (w.amp / 100);
                    }, 0) / Math.max(1, activeWaves.length);
                    
                    const petalLength = layerRadius * (1 + waveModulation * 0.3);
                    
                    // Draw petal
                    ctx.save();
                    ctx.rotate(angle);
                    
                    const gradient = ctx.createLinearGradient(0, 0, 0, petalLength);
                    gradient.addColorStop(0, `hsla(${depthHue}, 100%, 70%, ${alpha * 0.3})`);
                    gradient.addColorStop(0.5, `hsla(${(depthHue + 30) % 360}, 95%, 60%, ${alpha})`);
                    gradient.addColorStop(1, `hsla(${(depthHue + 60) % 360}, 90%, 50%, ${alpha * 0.1})`);
                    
                    ctx.fillStyle = gradient;
                    ctx.strokeStyle = `hsla(${(depthHue + 180) % 360}, 100%, 80%, ${alpha * 0.8})`;
                    ctx.lineWidth = 1 + scale * 2;
                    ctx.shadowColor = `hsla(${depthHue}, 100%, 70%, ${alpha})`;
                    
                    // Petal shape (curved triangle)
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.quadraticCurveTo(
                        petalLength * 0.3, -petalLength * 0.15,
                        petalLength, 0
                    );
                    ctx.quadraticCurveTo(
                        petalLength * 0.3, petalLength * 0.15,
                        0, 0
                    );
                    ctx.closePath();
                    ctx.fill();
                    ctx.stroke();
                    
                    ctx.restore();
                }
                
                // Center circle for each layer
                const centerSize = layerRadius * 0.15;
                const centerGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, centerSize);
                centerGradient.addColorStop(0, `hsla(${(depthHue + 180) % 360}, 100%, 90%, ${alpha})`);
                centerGradient.addColorStop(0.7, `hsla(${depthHue}, 100%, 70%, ${alpha * 0.6})`);
                centerGradient.addColorStop(1, `hsla(${depthHue}, 100%, 50%, 0)`);
                
                ctx.fillStyle = centerGradient;
                ctx.beginPath();
                ctx.arc(0, 0, centerSize, 0, Math.PI * 2);
                ctx.fill();
            }
            
            ctx.restore();
            
            // === KALEIDOSCOPIC FRACTAL ENTITIES ===
            const kaleidoSymmetry = 6 + Math.floor(intensity * 6); // 6-12 fold symmetry
            const entityComplexity = 5 + Math.floor(intensity * 5); // More complex with higher energy
            
            ctx.save();
            ctx.translate(cx, cy);
            
            // Multiple entity layers
            for (let layer = 0; layer < 3; layer++) {
                const layerRotation = time * hyperSpeed * (0.3 + layer * 0.2) + layer * Math.PI / 6;
                const layerScale = 0.6 - layer * 0.15;
                
                for (let sym = 0; sym < kaleidoSymmetry; sym++) {
                    ctx.save();
                    ctx.rotate((sym * 2 * Math.PI / kaleidoSymmetry) + layerRotation);
                    
                    // Draw fractal entity arm
                    const armLength = Math.min(width, height) * 0.35 * layerScale * zoom;
                    
                    for (let branch = 0; branch < entityComplexity; branch++) {
                        const t = branch / entityComplexity;
                        const branchPos = armLength * Math.pow(t, 0.8);
                        
                        // Wave-modulated position
                        const waveOffset = activeWaves.reduce((sum, w, idx) => {
                            const phase = (w.phase * Math.PI / 180) + time * w.freq * speed * 2;
                            return sum + Math.sin(phase + branch * 0.8 + sym * 0.3) * (w.amp / 200);
                        }, 0);
                        
                        const x = branchPos;
                        const y = waveOffset * armLength * 0.2;
                        
                        // Entity node
                        const nodeSize = (1 - t) * 8 * layerScale + 2;
                        const nodeHue = (hueShift + branch * 30 + sym * 15 + layer * 45 + time * 40) % 360;
                        
                        ctx.shadowBlur = 15;
                        ctx.shadowColor = `hsla(${nodeHue}, 100%, 70%, 0.8)`;
                        
                        const nodeGradient = ctx.createRadialGradient(x, y, 0, x, y, nodeSize);
                        nodeGradient.addColorStop(0, `hsla(${nodeHue}, 100%, 90%, 0.9)`);
                        nodeGradient.addColorStop(0.5, `hsla(${(nodeHue + 60) % 360}, 100%, 70%, 0.7)`);
                        nodeGradient.addColorStop(1, `hsla(${(nodeHue + 120) % 360}, 100%, 50%, 0.2)`);
                        
                        ctx.fillStyle = nodeGradient;
                        ctx.beginPath();
                        ctx.arc(x, y, nodeSize, 0, Math.PI * 2);
                        ctx.fill();
                        
                        // Connecting lines (entity structure)
                        if (branch > 0) {
                            const prevT = (branch - 1) / entityComplexity;
                            const prevBranchPos = armLength * Math.pow(prevT, 0.8);
                            const prevWaveOffset = activeWaves.reduce((sum, w) => {
                                const phase = (w.phase * Math.PI / 180) + time * w.freq * speed * 2;
                                return sum + Math.sin(phase + (branch - 1) * 0.8 + sym * 0.3) * (w.amp / 200);
                            }, 0);
                            const prevY = prevWaveOffset * armLength * 0.2;
                            
                            ctx.strokeStyle = `hsla(${nodeHue}, 100%, 60%, 0.4)`;
                            ctx.lineWidth = (1 - t) * 3 + 0.5;
                            ctx.beginPath();
                            ctx.moveTo(prevBranchPos, prevY);
                            ctx.lineTo(x, y);
                            ctx.stroke();
                        }
                        
                        // Sub-branches (more complexity)
                        if (branch % 2 === 0 && branch > 0) {
                            const subAngle = Math.sin(time * hyperSpeed + branch) * Math.PI / 3;
                            const subLength = nodeSize * 4;
                            const subX = x + Math.cos(subAngle) * subLength;
                            const subY = y + Math.sin(subAngle) * subLength;
                            
                            ctx.strokeStyle = `hsla(${(nodeHue + 90) % 360}, 100%, 70%, 0.3)`;
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(x, y);
                            ctx.lineTo(subX, subY);
                            ctx.stroke();
                            
                            ctx.fillStyle = `hsla(${(nodeHue + 90) % 360}, 100%, 80%, 0.5)`;
                            ctx.beginPath();
                            ctx.arc(subX, subY, nodeSize * 0.5, 0, Math.PI * 2);
                            ctx.fill();
                        }
                    }
                    
                    ctx.restore();
                }
            }
            
            ctx.restore();
            
            // === MORPHING GEOMETRIC LATTICE (Background patterns) ===
            const latticeSize = 40;
            const latticeScale = zoom * 1.5;
            
            ctx.shadowBlur = 5;
            
            for (let gx = -2; gx < Math.ceil(width / latticeSize) + 2; gx++) {
                for (let gy = -2; gy < Math.ceil(height / latticeSize) + 2; gy++) {
                    const x = gx * latticeSize * latticeScale + (pan.x % (latticeSize * latticeScale));
                    const y = gy * latticeSize * latticeScale + (pan.y % (latticeSize * latticeScale));
                    
                    // Wave-based distortion
                    const distortion = activeWaves.reduce((sum, w) => {
                        const phase = (w.phase * Math.PI / 180) + time * w.freq * speed;
                        return sum + Math.sin(phase + gx * 0.5 + gy * 0.3) * (w.amp / 50);
                    }, 0);
                    
                    const finalX = x + distortion * 10;
                    const finalY = y + distortion * 10;
                    
                    const cellHue = (hueShift + gx * 20 + gy * 20 + time * 20) % 360;
                    const pulse = Math.abs(Math.sin(time * hyperSpeed + gx * 0.4 + gy * 0.4));
                    
                    // Geometric cell shapes (triangle, square, hexagon alternating)
                    const shapeType = (gx + gy) % 3;
                    const size = 6 + pulse * 4;
                    
                    ctx.fillStyle = `hsla(${cellHue}, 100%, 60%, ${0.15 + pulse * 0.15})`;
                    ctx.strokeStyle = `hsla(${cellHue}, 100%, 80%, ${0.3 + pulse * 0.2})`;
                    ctx.lineWidth = 1;
                    
                    ctx.beginPath();
                    if (shapeType === 0) {
                        // Triangle
                        for (let i = 0; i < 3; i++) {
                            const angle = (i * 2 * Math.PI / 3) + time * hyperSpeed;
                            const px = finalX + Math.cos(angle) * size;
                            const py = finalY + Math.sin(angle) * size;
                            if (i === 0) ctx.moveTo(px, py);
                            else ctx.lineTo(px, py);
                        }
                        ctx.closePath();
                    } else if (shapeType === 1) {
                        // Square
                        ctx.rect(finalX - size/2, finalY - size/2, size, size);
                    } else {
                        // Hexagon
                        for (let i = 0; i < 6; i++) {
                            const angle = (i * 2 * Math.PI / 6) + time * hyperSpeed * 0.5;
                            const px = finalX + Math.cos(angle) * size;
                            const py = finalY + Math.sin(angle) * size;
                            if (i === 0) ctx.moveTo(px, py);
                            else ctx.lineTo(px, py);
                        }
                        ctx.closePath();
                    }
                    ctx.fill();
                    ctx.stroke();
                }
            }
            
            // === ENERGY WISPS (Floating organic forms) ===
            const numWisps = 20 + Math.floor(intensity * 30);
            
            for (let i = 0; i < numWisps; i++) {
                const wispAngle = (i / numWisps) * Math.PI * 2 + time * hyperSpeed * 0.3;
                const wispRadius = (Math.min(width, height) * 0.3) + Math.sin(time * 2 + i) * 100;
                
                const wx = cx + Math.cos(wispAngle) * wispRadius;
                const wy = cy + Math.sin(wispAngle) * wispRadius;
                
                const wispHue = (hueShift + i * 15 + time * 60) % 360;
                const wispSize = 10 + Math.sin(time * 3 + i * 0.5) * 5;
                
                ctx.shadowBlur = 20;
                ctx.shadowColor = `hsla(${wispHue}, 100%, 70%, 0.6)`;
                
                const wispGradient = ctx.createRadialGradient(wx, wy, 0, wx, wy, wispSize);
                wispGradient.addColorStop(0, `hsla(${wispHue}, 100%, 90%, 0.7)`);
                wispGradient.addColorStop(0.5, `hsla(${(wispHue + 40) % 360}, 100%, 70%, 0.4)`);
                wispGradient.addColorStop(1, `hsla(${(wispHue + 80) % 360}, 100%, 50%, 0)`);
                
                ctx.fillStyle = wispGradient;
                ctx.beginPath();
                ctx.arc(wx, wy, wispSize, 0, Math.PI * 2);
                ctx.fill();
                
                // Wisp trail
                const trailLength = 5;
                for (let t = 1; t <= trailLength; t++) {
                    const trailAngle = wispAngle - t * 0.1;
                    const trailRadius = wispRadius - t * 5;
                    const tx = cx + Math.cos(trailAngle) * trailRadius;
                    const ty = cy + Math.sin(trailAngle) * trailRadius;
                    const trailAlpha = (1 - t / trailLength) * 0.3;
                    
                    ctx.fillStyle = `hsla(${wispHue}, 100%, 70%, ${trailAlpha})`;
                    ctx.beginPath();
                    ctx.arc(tx, ty, wispSize * (1 - t / trailLength), 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            
            // === CENTRAL VORTEX (Singularity point) ===
            ctx.shadowBlur = 40;
            ctx.shadowColor = `hsla(${hueShift}, 100%, 80%, 0.9)`;
            
            const vortexPulse = 1 + Math.sin(time * hyperSpeed * 2) * 0.3;
            const vortexSize = 30 * vortexPulse * intensity;
            
            const vortexGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, vortexSize);
            vortexGradient.addColorStop(0, `hsla(${(hueShift + 180) % 360}, 100%, 100%, 1)`);
            vortexGradient.addColorStop(0.3, `hsla(${hueShift}, 100%, 80%, 0.9)`);
            vortexGradient.addColorStop(0.6, `hsla(${(hueShift + 120) % 360}, 100%, 60%, 0.6)`);
            vortexGradient.addColorStop(1, `hsla(${(hueShift + 240) % 360}, 100%, 40%, 0.1)`);
            
            ctx.fillStyle = vortexGradient;
            ctx.beginPath();
            ctx.arc(cx, cy, vortexSize, 0, Math.PI * 2);
            ctx.fill();
            
            // Vortex spiral arms
            for (let arm = 0; arm < 4; arm++) {
                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate((arm * Math.PI / 2) + time * hyperSpeed);
                
                ctx.strokeStyle = `hsla(${(hueShift + arm * 90) % 360}, 100%, 80%, 0.6)`;
                ctx.lineWidth = 3;
                ctx.beginPath();
                
                for (let i = 0; i < 50; i++) {
                    const t = i / 50;
                    const spiralAngle = t * Math.PI * 3;
                    const spiralRadius = t * vortexSize * 2;
                    
                    const sx = Math.cos(spiralAngle) * spiralRadius;
                    const sy = Math.sin(spiralAngle) * spiralRadius;
                    
                    if (i === 0) ctx.moveTo(sx, sy);
                    else ctx.lineTo(sx, sy);
                }
                ctx.stroke();
                ctx.restore();
            }
            
            ctx.shadowBlur = 0;
        }

        // --- AUDIO BLOOM VISUALIZATION (Music Reactive) ---
        else if (viewMode === 'audiobloom') {
            // Dark stage background
            const bgGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(width, height));
            bgGradient.addColorStop(0, '#0a0a1a');
            bgGradient.addColorStop(0.6, '#050510');
            bgGradient.addColorStop(1, '#000000');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, width, height);
            
            const numBands = Math.max(16, Math.min(64, activeWaves.length * 2));
            const baseRadius = Math.min(width, height) * 0.15 * zoom;
            const maxRadius = Math.min(width, height) * 0.45 * zoom;
            
            // Calculate frequency spectrum - MUSIC OPTIMIZED
            const spectrum: number[] = [];
            
            // For uploaded audio, map directly to frequency bands
            if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
                // Create logarithmic frequency bands (20 Hz - 20 kHz)
                for (let i = 0; i < numBands; i++) {
                    const minFreq = 20;
                    const maxFreq = 20000;
                    const logMin = Math.log(minFreq);
                    const logMax = Math.log(maxFreq);
                    const bandFreq = Math.exp(logMin + (i / numBands) * (logMax - logMin));
                    const nextBandFreq = Math.exp(logMin + ((i + 1) / numBands) * (logMax - logMin));
                    
                    let bandEnergy = 0;
                    
                    // Sum up all audio frequencies in this band
                    for (let j = 0; j < uploadedAudioData.length; j += 3) {
                        if (j + 2 < uploadedAudioData.length) {
                            const audioFreq = uploadedAudioData[j];
                            const audioAmp = uploadedAudioData[j + 1];
                            
                            if (audioFreq >= bandFreq && audioFreq < nextBandFreq) {
                                bandEnergy += audioAmp * 100;
                            }
                        }
                    }
                    
                    spectrum.push(bandEnergy);
                }
            } else {
                // Fallback for synthesized waves
                for (let i = 0; i < numBands; i++) {
                    const freqRange = (i / numBands) * 100;
                    const bandWidth = 100 / numBands;
                    
                    let bandEnergy = 0;
                    
                    activeWaves.forEach(wave => {
                        const effectiveFreq = wave.freq * speed;
                        if (effectiveFreq >= freqRange && effectiveFreq < freqRange + bandWidth) {
                            const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
                            const amplitude = wave.amp * Math.abs(Math.sin(phase)) * 2;
                            bandEnergy += amplitude;
                        } else {
                            const distance = Math.abs(effectiveFreq - freqRange);
                            if (distance < bandWidth * 4) {
                                const falloff = 1 - (distance / (bandWidth * 4));
                                const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
                                bandEnergy += wave.amp * Math.abs(Math.sin(phase)) * falloff * 0.8;
                            }
                        }
                    });
                    
                    spectrum.push(bandEnergy);
                }
            }
            
            // Smooth spectrum (temporal smoothing for music feel)
            const smoothedSpectrum = spectrum.map((val, i) => {
                if (i === 0 || i === spectrum.length - 1) return val;
                return (spectrum[i - 1] + val * 2 + spectrum[i + 1]) / 4;
            });
            
            // Calculate total energy and bass energy
            const totalEnergy = smoothedSpectrum.reduce((sum, val) => sum + val, 0);
            const avgEnergy = totalEnergy / numBands;
            const bassEnergy = smoothedSpectrum.slice(0, Math.floor(numBands * 0.2)).reduce((sum, val) => sum + val, 0);
            const midEnergy = smoothedSpectrum.slice(Math.floor(numBands * 0.2), Math.floor(numBands * 0.6)).reduce((sum, val) => sum + val, 0);
            const trebleEnergy = smoothedSpectrum.slice(Math.floor(numBands * 0.6)).reduce((sum, val) => sum + val, 0);
            
            // Beat detection (bass threshold)
            const beatThreshold = avgEnergy * 1.5;
            const isBeat = bassEnergy > beatThreshold;
            
            // === BASS PULSE CORE ===
            const corePulse = isBeat ? 1.5 : 1 + (bassEnergy / 100) * 0.3;
            const coreRadius = baseRadius * 0.4 * corePulse;
            
            ctx.shadowBlur = isBeat ? 40 : 20;
            ctx.shadowColor = `rgba(255, ${Math.floor(100 + bassEnergy)}, 150, 0.8)`;
            
            const coreGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreRadius);
            coreGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 * corePulse})`);
            coreGradient.addColorStop(0.4, `rgba(255, ${Math.floor(150 + bassEnergy)}, 200, ${0.7 * corePulse})`);
            coreGradient.addColorStop(0.8, `rgba(200, 100, 255, ${0.4 * corePulse})`);
            coreGradient.addColorStop(1, 'rgba(100, 50, 200, 0)');
            
            ctx.fillStyle = coreGradient;
            ctx.beginPath();
            ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
            ctx.fill();
            
            // === CIRCULAR SPECTRUM BARS ===
            smoothedSpectrum.forEach((energy, i) => {
                const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
                const normalizedEnergy = Math.min(1, energy / 100);
                
                // Color based on frequency (bass=red, mid=green, treble=blue)
                const freqRatio = i / numBands;
                let r, g, b;
                if (freqRatio < 0.33) {
                    // Bass - Red to Orange
                    r = 255;
                    g = Math.floor(freqRatio * 3 * 180);
                    b = 50;
                } else if (freqRatio < 0.67) {
                    // Mid - Green to Cyan
                    r = Math.floor((1 - (freqRatio - 0.33) * 3) * 180);
                    g = 255;
                    b = Math.floor((freqRatio - 0.33) * 3 * 200);
                } else {
                    // Treble - Blue to Purple
                    r = Math.floor((freqRatio - 0.67) * 3 * 200);
                    g = 50;
                    b = 255;
                }
                
                const barHeight = normalizedEnergy * (maxRadius - baseRadius);
                const barInnerRadius = baseRadius;
                const barOuterRadius = baseRadius + barHeight;
                
                // Draw bar
                const barWidth = (Math.PI * 2 / numBands) * 0.8;
                
                ctx.shadowBlur = 10 + normalizedEnergy * 20;
                ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${0.6 + normalizedEnergy * 0.4})`;
                
                const gradient = ctx.createLinearGradient(
                    cx + Math.cos(angle) * barInnerRadius,
                    cy + Math.sin(angle) * barInnerRadius,
                    cx + Math.cos(angle) * barOuterRadius,
                    cy + Math.sin(angle) * barOuterRadius
                );
                gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.3)`);
                gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${0.6 + normalizedEnergy * 0.3})`);
                gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${0.8 + normalizedEnergy * 0.2})`);
                
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(cx, cy, barOuterRadius, angle - barWidth / 2, angle + barWidth / 2);
                ctx.arc(cx, cy, barInnerRadius, angle + barWidth / 2, angle - barWidth / 2, true);
                ctx.closePath();
                ctx.fill();
                
                // Bright tip
                if (normalizedEnergy > 0.3) {
                    const tipX = cx + Math.cos(angle) * barOuterRadius;
                    const tipY = cy + Math.sin(angle) * barOuterRadius;
                    
                    ctx.fillStyle = `rgba(255, 255, 255, ${normalizedEnergy * 0.8})`;
                    ctx.beginPath();
                    ctx.arc(tipX, tipY, 2 + normalizedEnergy * 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            });
            
            // === WAVEFORM RING ===
            const waveRingRadius = maxRadius * 1.2;
            const wavePoints = 120;
            
            ctx.strokeStyle = `rgba(100, 200, 255, 0.5)`;
            ctx.lineWidth = 2;
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'rgba(100, 200, 255, 0.6)';
            
            ctx.beginPath();
            for (let i = 0; i <= wavePoints; i++) {
                const t = (i / wavePoints) * Math.PI * 2;
                
                // Calculate waveform at this angle
                let waveValue = 0;
                activeWaves.forEach(wave => {
                    const effectiveFreq = wave.freq * speed;
                    const phase = (wave.phase * Math.PI / 180) + time * effectiveFreq;
                    waveValue += wave.amp * Math.sin(phase + t * 3) / 100;
                });
                
                const radius = waveRingRadius + waveValue * 30;
                const x = cx + Math.cos(t) * radius;
                const y = cy + Math.sin(t) * radius;
                
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.stroke();
            
            // === PARTICLE BURST SYSTEM ===
            const particlesPerBand = 3;
            
            smoothedSpectrum.forEach((energy, i) => {
                if (energy < 10) return; // Skip quiet bands
                
                const angle = (i / numBands) * Math.PI * 2 - Math.PI / 2;
                const normalizedEnergy = Math.min(1, energy / 100);
                const barRadius = baseRadius + normalizedEnergy * (maxRadius - baseRadius);
                
                // Color based on frequency
                const freqRatio = i / numBands;
                let r, g, b;
                if (freqRatio < 0.33) {
                    r = 255; g = Math.floor(freqRatio * 3 * 180); b = 50;
                } else if (freqRatio < 0.67) {
                    r = Math.floor((1 - (freqRatio - 0.33) * 3) * 180); g = 255; b = Math.floor((freqRatio - 0.33) * 3 * 200);
                } else {
                    r = Math.floor((freqRatio - 0.67) * 3 * 200); g = 50; b = 255;
                }
                
                for (let p = 0; p < particlesPerBand; p++) {
                    const particlePhase = (time * 2 + i * 0.3 + p * 0.5) % 1;
                    const particleDistance = barRadius + particlePhase * (maxRadius - barRadius) * 0.6;
                    
                    const spreadAngle = angle + (Math.sin(time * 3 + p) * 0.15);
                    const px = cx + Math.cos(spreadAngle) * particleDistance;
                    const py = cy + Math.sin(spreadAngle) * particleDistance;
                    
                    const particleSize = (1 - particlePhase) * normalizedEnergy * 4 + 1;
                    const particleAlpha = (1 - particlePhase) * normalizedEnergy;
                    
                    ctx.shadowBlur = 8;
                    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${particleAlpha * 0.8})`;
                    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${particleAlpha})`;
                    ctx.beginPath();
                    ctx.arc(px, py, particleSize, 0, Math.PI * 2);
                    ctx.fill();
                }
            });
            
            // === BEAT RINGS (Expanding on beat) ===
            if (isBeat) {
                const beatPhase = (time * 3) % 1;
                const beatRadius = baseRadius + beatPhase * (maxRadius - baseRadius) * 1.5;
                const beatAlpha = (1 - beatPhase) * 0.6;
                
                ctx.strokeStyle = `rgba(255, 100, 200, ${beatAlpha})`;
                ctx.lineWidth = 4 * (1 - beatPhase);
                ctx.shadowBlur = 20;
                ctx.shadowColor = `rgba(255, 100, 200, ${beatAlpha})`;
                ctx.beginPath();
                ctx.arc(cx, cy, beatRadius, 0, Math.PI * 2);
                ctx.stroke();
                
                // Secondary ring
                const beat2Radius = baseRadius + (beatPhase * 0.7) * (maxRadius - baseRadius) * 1.5;
                const beat2Alpha = (1 - beatPhase * 0.7) * 0.4;
                ctx.strokeStyle = `rgba(150, 100, 255, ${beat2Alpha})`;
                ctx.lineWidth = 3 * (1 - beatPhase);
                ctx.beginPath();
                ctx.arc(cx, cy, beat2Radius, 0, Math.PI * 2);
                ctx.stroke();
            }
            
            // === FREQUENCY LINES (Connecting inner to outer) ===
            const numLines = 12;
            for (let i = 0; i < numLines; i++) {
                const angle = (i / numLines) * Math.PI * 2;
                const bandIndex = Math.floor((i / numLines) * numBands);
                const energy = smoothedSpectrum[bandIndex] || 0;
                const normalizedEnergy = Math.min(1, energy / 100);
                
                const opacity = normalizedEnergy * 0.3;
                const lineLength = baseRadius + normalizedEnergy * (maxRadius - baseRadius) * 0.7;
                
                ctx.strokeStyle = `rgba(200, 150, 255, ${opacity})`;
                ctx.lineWidth = 1;
                ctx.shadowBlur = 5;
                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.lineTo(cx + Math.cos(angle) * lineLength, cy + Math.sin(angle) * lineLength);
                ctx.stroke();
            }
            
            // === ENERGY LEVEL INDICATORS ===
            const indicatorY = 70;
            const indicatorWidth = 60;
            const indicatorHeight = 8;
            const spacing = 10;
            
            // Bass indicator
            ctx.fillStyle = 'rgba(255, 100, 100, 0.3)';
            ctx.fillRect(20, indicatorY, indicatorWidth, indicatorHeight);
            ctx.fillStyle = `rgba(255, 100, 100, 0.8)`;
            ctx.fillRect(20, indicatorY, (bassEnergy / 100) * indicatorWidth, indicatorHeight);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.font = '10px sans-serif';
            ctx.fillText('BASS', 20, indicatorY - 4);
            
            // Mid indicator
            ctx.fillStyle = 'rgba(100, 255, 100, 0.3)';
            ctx.fillRect(20, indicatorY + indicatorHeight + spacing, indicatorWidth, indicatorHeight);
            ctx.fillStyle = `rgba(100, 255, 100, 0.8)`;
            ctx.fillRect(20, indicatorY + indicatorHeight + spacing, (midEnergy / 100) * indicatorWidth, indicatorHeight);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.fillText('MID', 20, indicatorY + indicatorHeight + spacing - 4);
            
            // Treble indicator
            ctx.fillStyle = 'rgba(100, 100, 255, 0.3)';
            ctx.fillRect(20, indicatorY + (indicatorHeight + spacing) * 2, indicatorWidth, indicatorHeight);
            ctx.fillStyle = `rgba(100, 100, 255, 0.8)`;
            ctx.fillRect(20, indicatorY + (indicatorHeight + spacing) * 2, (trebleEnergy / 100) * indicatorWidth, indicatorHeight);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.fillText('TREBLE', 20, indicatorY + (indicatorHeight + spacing) * 2 - 4);
            
            ctx.shadowBlur = 0;
        }

    }, [waves, viewMode, zoom, pan, height, isAutoRotating, speed, isFullBrowser, showUIControls]);

    // Status logic
    React.useEffect(() => {
        const activeWaves = waves.filter(isActive);
        const totalAmp = activeWaves.reduce((s, w) => s + w.amp, 0);
        
        if (totalAmp === 0) {
            setStatus({ text: 'Silence', type: 'silence' });
            return;
        }

        if (viewMode === 'time' && activeWaves.length > 1) {
            let maxObserved = 0;
            for (let t = 0; t < 1; t += 0.05) {
                let ySum = 0;
                for (const wave of activeWaves) {
                    const phaseRad = (wave.phase * Math.PI) / 180;
                    ySum += wave.amp * Math.sin(2 * Math.PI * wave.freq * t + phaseRad);
                }
                if (Math.abs(ySum) > maxObserved) maxObserved = Math.abs(ySum);
            }

            if (maxObserved < totalAmp * 0.1 && totalAmp > 10) {
                 setStatus({ text: 'Destructive Interference', type: 'destructive' });
                 return;
            }
        }
        
        if (viewMode === 'lissajous') {
             if (activeWaves.length < 2) setStatus({ text: 'Lissajous needs 2+ layers', type: 'normal' });
             else setStatus({ text: 'XY Phase Plot', type: 'normal' });
        } else if (viewMode === 'xyz') {
             if (activeWaves.length < 3) setStatus({ text: 'XYZ needs 3 layers (X, Y, Z)', type: 'normal' });
             else setStatus({ text: 'XYZ 3D Lissajous Knot', type: 'normal' });
        } else if (viewMode === 'chladni') {
             setStatus({ text: `Rigid Plate Mode (Zoom: ${zoom.toFixed(1)}x)`, type: 'normal' });
        } else if (viewMode === 'fluid') {
             setStatus({ text: 'Non-Newtonian Simulation (3D)', type: 'normal' });
        } else if (viewMode === 'water') {
             setStatus({ text: 'Wave Tank (Boundary Reflections)', type: 'normal' });
        } else {
             setStatus({ text: 'Composite Wave', type: 'normal' });
        }
        
    }, [waves, viewMode, zoom]);

    const statusColors = {
        normal: 'bg-gray-800 text-gray-400',
        destructive: 'bg-red-900/50 text-red-200 border border-red-500/50 animate-pulse',
        silence: 'bg-gray-800 text-gray-600'
    };

    // Interaction Handlers (Mouse Drag only)
    const handleMouseDown = (e: React.MouseEvent) => {
        if (viewMode !== 'chladni' && viewMode !== 'fluid' && viewMode !== 'water' && viewMode !== 'xyz') return;
        
        // Pause auto-rotation for XYZ and Fluid when interacting
        if (viewMode === 'xyz' || viewMode === 'fluid') {
            setIsAutoRotating(false);
        }

        setIsDragging(true);
        lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        // Update mouse position for tooltip tracking
        const canvas = canvasRef.current;
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            setMousePos({ x, y });
        }
        
        if (!isDragging || (viewMode !== 'oscilloscope' && viewMode !== 'oscilloscope-xy' && viewMode !== 'chladni' && viewMode !== 'fluid' && viewMode !== 'water' && viewMode !== 'xyz')) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        lastPos.current = { x: e.clientX, y: e.clientY };
        
        setViewSettings(prev => {
            const current = prev[viewMode];
            return {
                ...prev,
                [viewMode]: {
                    ...current,
                    pan: { x: current.pan.x + dx, y: current.pan.y + dy }
                }
            };
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Full-browser toggle
    const toggleFullBrowser = () => {
        const newState = !isFullBrowser;
        setIsFullBrowser(newState);
        if (onTogglePanels) {
            onTogglePanels(newState);
        }
    };

    return (
        <div className={`bg-gray-900 shadow-xl border border-gray-800 relative transition-all duration-75 flex flex-col ${
            isFullBrowser ? 'h-full pt-3 px-4' : 'rounded-xl p-4 pb-6'
        }`}>
            {showUIControls && (
            <div className="flex flex-col md:flex-row justify-between items-center mb-3 gap-3 flex-none">
                <div className="flex items-center gap-3 w-full md:w-auto">
                    {/* UI Controls Toggle */}
                    <button
                        onClick={() => setShowUIControls(!showUIControls)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-2 ${
                            showUIControls 
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-500'
                        }`}
                        title={showUIControls ? 'Hide UI Controls' : 'Show UI Controls'}
                    >
                        {showUIControls ? (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                                Hide UI
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                                </svg>
                                Show UI
                            </>
                        )}
                    </button>

                    {/* Auto Cycle toggle + duration input */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsAutoCycle(!isAutoCycle)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-2 ${
                                isAutoCycle
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-500'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                            }`}
                            title={isAutoCycle ? 'Disable auto cycle' : 'Enable auto cycle'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a1 1 0 011-1h4a1 1 0 010 2H6.414l1.293 1.293a1 1 0 01-1.414 1.414L4 6.414V9a1 1 0 11-2 0V5a1 1 0 011-1zm12 12a1 1 0 01-1 1h-4a1 1 0 110-2h2.586l-1.293-1.293a1 1 0 111.414-1.414L16 13.586V11a1 1 0 112 0v4z" clipRule="evenodd" />
                            </svg>
                            {isAutoCycle ? 'Auto Cycle: On' : 'Auto Cycle: Off'}
                        </button>
                        <label className="flex items-center gap-1 text-xs text-gray-300">
                            <span>Every</span>
                            <input
                                type="number"
                                min={5}
                                max={600}
                                step={5}
                                value={cycleDuration}
                                onChange={(e) => setCycleDuration(Math.max(1, Number(e.target.value) || 30))}
                                className="w-16 px-2 py-1 rounded border border-gray-700 bg-gray-900 text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Auto cycle duration (seconds)"
                                title="Auto cycle duration (seconds)"
                            />
                            <span>sec</span>
                        </label>
                    </div>

                    {/* Frequency Display Toggle - Always visible */}
                    <button
                        onClick={() => setShowFrequencyDisplay(!showFrequencyDisplay)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-2 ${
                            showFrequencyDisplay
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                            : 'bg-cyan-600 text-white hover:bg-cyan-700 border border-cyan-500'
                        }`}
                        title={showFrequencyDisplay ? 'Hide frequency display' : 'Show frequency display'}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                        </svg>
                        {showFrequencyDisplay ? 'Hide Freq' : 'Show Freq'}
                    </button>

                    {/* Song Info Toggle - Only visible when music is playing */}
                    {isUploadedAudioPlaying && (
                        <button
                            onClick={() => setShowSongInfo(!showSongInfo)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-2 ${
                                showSongInfo
                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-500'
                            }`}
                            title={showSongInfo ? 'Hide song info' : 'Show song info'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                            </svg>
                            {showSongInfo ? 'Hide Song' : 'Show Song'}
                        </button>
                    )}
                </div>
                
                <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                    <button 
                        onClick={toggleAudio}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors border ${
                            isPlaying 
                            ? 'bg-amber-500/10 text-amber-500 border-amber-500/50 animate-pulse' 
                            : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
                        }`}
                    >
                        {isPlaying ? (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                                </svg>
                                Audio
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                Muted
                            </>
                        )}
                    </button>
                    
                    {/* Audio-Driven Visualization Indicator */}
                    {isUploadedAudioPlaying && (
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/50 animate-pulse">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                            </svg>
                            Audio Driven
                        </div>
                    )}
                    
                    <div className={`text-xs font-mono px-3 py-1 rounded transition-all duration-300 ${statusColors[status.type]}`}>
                        {status.text}
                    </div>
                </div>
            </div>
            )}
            
            <div 
                ref={containerRef}
                className="relative w-full bg-gray-950 rounded-lg overflow-hidden ring-1 ring-white/10"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ 
                    cursor: (viewMode === 'chladni' || viewMode === 'fluid' || viewMode === 'water' || viewMode === 'xyz' || viewMode === 'galaxy' || viewMode === 'sacred' || viewMode === 'dmt' || viewMode === 'audiobloom') ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    height: isFullBrowser ? (showUIControls ? 'calc(100vh - 4rem)' : 'calc(100vh - 1.5rem)') : `${height}px`,
                    position: 'relative',
                    width: '100%'
                }}
            >
                {/* NEW MODULAR VISUALIZER SYSTEM - USE THIS */}
                <VisualizerRenderer
                    viewMode={viewMode}
                    waves={waves}
                    speed={speed}
                    uploadedAudioData={uploadedAudioData}
                    waveformData={waveformData}
                    isUploadedAudioPlaying={isUploadedAudioPlaying}
                    width={containerRef.current?.clientWidth || 800}
                    height={isFullBrowser ? (showUIControls ? window.innerHeight - 64 : window.innerHeight - 24) : height}
                    zoom={viewSettings[viewMode].zoom}
                    pan={viewSettings[viewMode].pan}
                    isAutoRotating={isAutoRotating}
                    autoRotRef={autoRotRef.current}
                    smoothedMusicData={smoothedMusicDataRef.current}
                    mousePos={mousePos}
                    colorMode={colorMode}
                    mergeChannels={mergeChannels}
                    onAutoRotUpdate={(newValue) => { autoRotRef.current = newValue; }}
                    onHoveredFrequency={setHoveredFrequency}
                />
                
                {/* OLD SYSTEM - REMOVE THIS AFTER TESTING
                {viewMode === 'oscilloscope-xy' ? (
                    <OscilloscopeXYWebGL 
                        waveformData={waveformData || syntheticWaveform}
                        width={containerRef.current?.clientWidth || 800}
                        height={isFullBrowser ? (showUIControls ? window.innerHeight - 64 : window.innerHeight - 24) : height}
                        decayRate={0.55}
                    />
                ) : viewMode === 'waveform' ? (
                    <WaveformVisualizer 
                        waves={waves}
                        speed={speed}
                        height={isFullBrowser ? (showUIControls ? window.innerHeight - 64 : window.innerHeight - 24) : height}
                        uploadedAudioData={uploadedAudioData}
                        isUploadedAudioPlaying={isUploadedAudioPlaying}
                    />
                ) : (
                    <canvas ref={canvasRef} className="w-full h-full block" style={{ width: '100%', height: '100%', display: 'block', position: 'absolute', top: 0, left: 0 }} />
                )}
                */}
                
                {/* Show UI Button - Top Left (when hidden) */}
                {!showUIControls && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowUIControls(true);
                        }}
                        className="absolute top-2 left-2 p-2 rounded-lg bg-gray-900/30 hover:bg-gray-800/60 text-gray-500 hover:text-gray-300 border border-gray-700/30 hover:border-gray-600/50 backdrop-blur-sm transition-all z-10 shadow-lg"
                        title="Show UI Controls"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
                
                {/* Visualizer Selector Dropdown and Color Mode - Top Left */}
                {showUIControls && (
                    <div className="absolute top-2 left-2 flex gap-2 z-10" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 bg-gray-800/90 backdrop-blur-sm rounded-lg p-1 shadow-lg border border-gray-600">
                            <button
                                onClick={(e) => { e.stopPropagation(); cycleVisualizer('prev'); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-gray-700/80 transition-colors"
                                aria-label="Previous visualizer"
                                title="Previous visualizer"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.707 15.707a1 1 0 01-1.414 0L6.586 11l4.707-4.707a1 1 0 011.414 1.414L9.414 11l3.293 3.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                            </button>

                            <select
                                value={viewMode}
                                onChange={(e) => setViewMode(e.target.value as ViewMode)}
                                className="px-3 py-1.5 text-[11px] font-bold uppercase bg-gray-900/90 text-white border border-gray-600 rounded-md shadow-inner hover:bg-gray-800/90 transition-all cursor-pointer"
                            >
                                {['time', '2d', '3d', 'physics', 'artistic'].map(category => {
                                  const visualizers = getVisualizerRegistry().getByCategory(category as any);
                                  if (visualizers.length === 0) return null;
                                  
                                  const categoryLabel = {
                                    'time': 'Time Domain',
                                    '2d': '2D Patterns',
                                    '3d': '3D Visualizers',
                                    'physics': 'Physics Simulations',
                                    'artistic': 'Special Effects'
                                  }[category] || category;
                                  
                                  return (
                                    <optgroup key={category} label={categoryLabel}>
                                      {visualizers.map(v => (
                                        <option key={v.metadata.id} value={v.metadata.id}>
                                          {v.metadata.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  );
                                })}
                            </select>

                            <button
                                onClick={(e) => { e.stopPropagation(); cycleVisualizer('next'); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-gray-700/80 transition-colors"
                                aria-label="Next visualizer"
                                title="Next visualizer"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0L13.414 9l-4.707 4.707a1 1 0 01-1.414-1.414L10.586 9 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="flex bg-gray-800/90 backdrop-blur-sm rounded-lg p-0.5 shadow-lg">
                            <button
                                onClick={(e) => { e.stopPropagation(); setColorMode('wave'); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all whitespace-nowrap ${
                                    colorMode === 'wave'
                                    ? 'bg-gray-700/90 text-white border border-gray-500 shadow-sm'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-700/60'
                                }`}
                                title="Use individual wave colors"
                            >
                                Wave Color
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); setColorMode('channel'); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all whitespace-nowrap ${
                                    colorMode === 'channel'
                                    ? 'bg-gray-700/90 text-white border border-gray-500 shadow-sm'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-700/60'
                                }`}
                                title="Use channel-based coloring (L/R/Pan)"
                            >
                                Channel Color
                            </button>
                        </div>
                    </div>
                )}
                
                {viewMode === 'time' && (
                    <>
                        <div className="absolute top-1/2 left-0 w-full h-px bg-white opacity-10 pointer-events-none"></div>
                        {showUIControls && (
                            <button
                                onClick={(e) => { e.stopPropagation(); setMergeChannels(!mergeChannels); }}
                                title={mergeChannels ? 'Show separate L/R traces' : 'Merge into single waveform'}
                                className={`absolute top-2 right-2 px-2 py-1 text-[10px] font-bold rounded-md transition-all z-10 ${mergeChannels ? 'bg-emerald-600 text-white' : 'bg-gray-700/80 text-gray-300 hover:bg-gray-600'}`}
                            >
                                {mergeChannels ? '⟨⟩ Merged' : 'L|R Stereo'}
                            </button>
                        )}
                    </>
                )}

                {/* Zoom Display and Reset - Top Right (for zoomable views) */}
                {showUIControls && (viewMode === 'chladni' || viewMode === 'fluid' || viewMode === 'water' || viewMode === 'xyz' || viewMode === 'galaxy' || viewMode === 'sacred' || viewMode === 'dmt' || viewMode === 'audiobloom') && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-gray-800/90 backdrop-blur-sm rounded-lg p-1 z-10 shadow-lg" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <div className="px-2 py-1 text-[10px] font-mono text-gray-300">
                            {zoom.toFixed(1)}x
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const defaultZoom = viewMode === 'xyz' || viewMode === 'fluid' ? 0.8 : viewMode === 'chladni' ? 0.6 : 1;
                                setViewSettings(prev => ({
                                    ...prev,
                                    [viewMode]: { ...prev[viewMode], zoom: defaultZoom }
                                }));
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="px-2 py-1 text-[10px] font-bold rounded-md transition-all bg-gray-700/80 text-gray-300 hover:bg-gray-600 hover:text-white"
                            title="Reset zoom"
                        >
                            Reset
                        </button>
                    </div>
                )}

                {/* Full Browser Button - Bottom Left */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleFullBrowser();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                    className={`absolute bottom-4 left-4 p-2 rounded-lg backdrop-blur-sm transition-all border shadow-lg z-20 ${
                        isFullBrowser 
                        ? 'bg-gray-900/60 hover:bg-gray-800/70 text-gray-400 hover:text-gray-200 border-gray-700/50' 
                        : 'bg-gray-800/60 hover:bg-gray-700/70 text-gray-300 border-gray-600'
                    }`}
                    title={isFullBrowser ? 'Exit Full Browser' : 'Full Browser View'}
                >
                    {isFullBrowser ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 9V5H9L5 9zM5 11v4h4l-4-4zm6 4h4v-4l-4 4zm4-6V5h-4l4 4z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                    )}
                </button>

                {/* Raw/Filtered Frequency Toggle - Bottom Left (when music is playing) */}
                {showUIControls && isUploadedAudioPlaying && onShowRawFrequenciesChange && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onShowRawFrequenciesChange(!showRawFrequencies);
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        className={`absolute bottom-4 left-20 px-3 py-2 rounded-lg backdrop-blur-sm transition-all border shadow-lg z-20 text-xs font-semibold ${
                            showRawFrequencies
                            ? 'bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white border-gray-600' 
                            : 'bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white border-gray-600'
                        }`}
                        title={showRawFrequencies ? 'Showing raw music frequencies. Click to filter through frequency layers.' : 'Filtering music through frequency layers. Click to show raw frequencies.'}
                    >
                        {showRawFrequencies ? (
                            <span className="flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                                </svg>
                                Raw
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                                </svg>
                                Filtered
                            </span>
                        )}
                    </button>
                )}

                {/* Speed Slider - Bottom Center */}
                {showUIControls && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-gray-800/90 backdrop-blur-sm rounded-lg px-3 py-2 z-10 shadow-lg border border-gray-700" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <span className="text-[10px] text-gray-400 font-bold uppercase">Speed</span>
                        <input 
                            type="range" 
                            min="0" max="12" step="0.1" 
                            value={speed} 
                            onChange={e => onSpeedChange(parseFloat(e.target.value))}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                        />
                        <input
                            type="number"
                            value={speed}
                            min={0}
                            max={12}
                            step={0.1}
                            onChange={e => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val) && val >= 0 && val <= 12) {
                                    onSpeedChange(val);
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="text-xs font-mono text-gray-300 bg-gray-700 hover:bg-gray-600 focus:bg-gray-600 border border-gray-600 hover:border-gray-500 focus:border-indigo-500 rounded px-1.5 py-0.5 w-14 text-right focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button 
                            onClick={() => onSpeedChange(1)}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="text-[10px] text-gray-400 hover:text-white font-bold cursor-pointer transition-colors px-1"
                            title="Reset to 1x"
                        >
                            x
                        </button>
                    </div>
                )}

                {/* Resume Rotation Button for XYZ and Fluid */}
                {showUIControls && (viewMode === 'xyz' || viewMode === 'fluid' || viewMode === 'galaxy' || viewMode === 'sacred') && !isAutoRotating && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent drag start
                            setIsAutoRotating(true);
                        }}
                        className="absolute bottom-4 right-4 bg-gray-800/80 hover:bg-gray-700 text-white p-2 rounded-full backdrop-blur-sm transition-all border border-gray-600 shadow-lg z-20 group"
                        title="Resume Auto-Rotation"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:animate-spin" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}

                {/* Current Frequency Display - Left Middle (Draggable) */}
                {showFrequencyDisplay && hasAnyFrequency && (
                    <div 
                        className="absolute bg-gray-900/90 backdrop-blur-sm rounded-lg px-3 py-2 z-10 shadow-lg border border-gray-700/50 cursor-move hover:bg-gray-900/95 transition-colors"
                        style={{
                            left: `${frequencyDisplayPos.x}px`,
                            top: `${frequencyDisplayPos.y}px`,
                            transform: 'translate(-50%, -50%)'
                        }}
                        onMouseDown={(e) => {
                            isFrequencyDragging.current = true;
                            frequencyDragStart.current = {
                                x: e.clientX,
                                y: e.clientY,
                                posX: frequencyDisplayPos.x,
                                posY: frequencyDisplayPos.y
                            };
                            document.body.style.cursor = 'grabbing';
                            e.preventDefault();
                        }}
                        title="Drag to reposition"
                    >
                        <div className="flex flex-col gap-1">
                            {leftFrequencies.length > 0 && (
                                <div className="flex flex-col gap-0.5">
                                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: leftFrequencies[0]?.color || channelColors.left }}>L:</div>
                                    {leftFrequencies.map((f, i) => (
                                        <div key={i} className="text-xs font-mono pl-2" style={{ color: f.color || channelColors.left, opacity: 1 - (i * 0.15) }}>
                                            {f.freq.toFixed(1)}Hz
                                        </div>
                                    ))}
                                </div>
                            )}
                            {bothFrequencies.length > 0 && (
                                <div className="flex flex-col gap-0.5">
                                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: bothFrequencies[0]?.color || channelColors.both }}>C:</div>
                                    {bothFrequencies.map((f, i) => (
                                        <div key={i} className="text-xs font-mono pl-2" style={{ color: f.color || channelColors.both, opacity: 1 - (i * 0.15) }}>
                                            {f.freq.toFixed(1)}Hz
                                        </div>
                                    ))}
                                </div>
                            )}
                            {rightFrequencies.length > 0 && (
                                <div className="flex flex-col gap-0.5">
                                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: rightFrequencies[0]?.color || channelColors.right }}>R:</div>
                                    {rightFrequencies.map((f, i) => (
                                        <div key={i} className="text-xs font-mono pl-2" style={{ color: f.color || channelColors.right, opacity: 1 - (i * 0.15) }}>
                                            {f.freq.toFixed(1)}Hz
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Song Info Display - Toggle between left and right */}
                {showSongInfo && isUploadedAudioPlaying && currentSong && (
                    <div 
                        onClick={() => setSongInfoPosition(songInfoPosition === 'left' ? 'right' : 'left')}
                        className={`absolute top-14 ${songInfoPosition === 'left' ? 'left-2' : 'right-2'} flex flex-col items-center justify-start gap-1 bg-gradient-to-r from-purple-900/70 to-indigo-900/70 backdrop-blur-md rounded-lg px-4 py-2 z-10 shadow-2xl border border-purple-500/30 max-w-sm cursor-pointer transition-all duration-300 hover:from-purple-800/80 hover:to-indigo-800/80`}
                        title="Click to toggle position"
                    >
                        <div className="text-lg font-semibold text-white truncate text-center">
                            {currentSong.name}
                        </div>
                        <div className="text-base text-purple-200 truncate text-center">
                            {currentSong.artist || 'Unknown Artist'}
                        </div>
                    </div>
                )}
            </div>
            {!isFullBrowser && showUIControls && (viewMode === 'chladni' || viewMode === 'fluid' || viewMode === 'water' || viewMode === 'xyz' || viewMode === 'galaxy' || viewMode === 'sacred' || viewMode === 'dmt' || viewMode === 'audiobloom') && (
                <div className="mt-2 text-[10px] text-gray-500 flex justify-between px-1 flex-none">
                    <span className="flex items-center gap-2">
                        {viewMode === 'chladni' ? (
                            <>
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 bg-gray-800 border border-gray-600 rounded-sm"></span>
                                    <span>Quiet</span>
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 bg-amber-200 rounded-sm"></span>
                                    <span>Loud</span>
                                </span>
                            </>
                        ) : viewMode === 'water' ? (
                            <span className="italic text-blue-400/70">
                                2D Ripple Tank
                            </span>
                        ) : viewMode === 'xyz' ? (
                            <span className="italic text-purple-500/70">
                                3D Lissajous Phase Plot
                            </span>
                        ) : viewMode === 'galaxy' ? (
                            <span className="italic text-violet-500/70">
                                Spiral Galaxy Simulation
                            </span>
                        ) : viewMode === 'sacred' ? (
                            <span className="italic text-yellow-500/70">
                                Sacred Geometry Mandala
                            </span>
                        ) : viewMode === 'dmt' ? (
                            <span className="italic text-fuchsia-500/70">
                                Hyperspace Breakthrough
                            </span>
                        ) : viewMode === 'audiobloom' ? (
                            <span className="italic text-emerald-500/70">
                                Real-Time Music Spectrum
                            </span>
                        ) : (
                            <span className="italic text-cyan-600/70">
                                3D Simulation
                            </span>
                        )}
                    </span>
                    <span className="font-medium text-amber-600">Scroll to Zoom • Drag to Pan</span>
                </div>
            )}
            
            {/* Resizing Handle */}
            {!isFullBrowser && (
            <div 
                className="absolute bottom-0 left-0 right-0 h-4 flex items-center justify-center cursor-ns-resize group hover:bg-gray-800/50 transition-colors rounded-b-xl"
                onMouseDown={startResize}
            >
                <div className="w-12 h-1 rounded-full bg-gray-700 group-hover:bg-gray-500 transition-colors"></div>
            </div>
            )}
        </div>
    );
};